import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import Papa from 'papaparse';
import { AuthProvider, useAuth } from './AuthContext';
import * as API from './api';

// ─── SOCKET ──────────────────────────────────────────────────
let socket;
function getSocket() {
    if (!socket) {
    socket = io(window.location.origin, {
      path: '/socket.io',
      transports: ['polling'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 3000,
    });
    socket.on('connect', () => console.log('✅ Socket connected:', socket.id));
    socket.on('connect_error', (e) => console.error('❌ Socket error:', e.message));
  }

  return socket;
}

// ─── DESIGN TOKENS ───────────────────────────────────────────
const C = {
  wa:'#25D366', waDark:'#128C7E', waLight:'#E8FAF0',
  bg:'#F4F6F9', surface:'#FFFFFF', card:'#FFFFFF',
  border:'#E4E8EF', text:'#111827', textSub:'#4B5563',
  muted:'#9CA3AF', accent:'#2563EB', accentBg:'#EFF6FF',
  success:'#16A34A', successBg:'#F0FDF4',
  warning:'#D97706', warningBg:'#FFFBEB',
  danger:'#DC2626',  dangerBg:'#FEF2F2',
  purple:'#7C3AED',  purpleBg:'#F5F3FF',
  shadow:'0 1px 3px rgba(0,0,0,0.08)',
};
const F = "'DM Sans',system-ui,sans-serif";

// ─── SHARED UI ────────────────────────────────────────────────
const Av = ({ text='?', size=36, bg=C.accentBg, color=C.accent }) => (
  <div style={{ width:size, height:size, borderRadius:'50%', background:bg, color,
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:size*0.34, fontWeight:700, flexShrink:0, border:`1.5px solid ${color}30` }}>{text}</div>
);
const Badge = ({ label, color=C.accent, bg=C.accentBg }) => (
  <span style={{ background:bg, color, padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:600, display:'inline-flex', alignItems:'center', gap:3 }}>{label}</span>
);
const StatusBadge = ({ status }) => {
  const m = { active:{bg:C.successBg,color:C.success,label:'● Active'}, paused:{bg:C.warningBg,color:C.warning,label:'⏸ Paused'},
    scheduled:{bg:C.purpleBg,color:C.purple,label:'◷ Scheduled'}, completed:{bg:'#F3F4F6',color:'#6B7280',label:'✓ Completed'},
    connected:{bg:C.successBg,color:C.success,label:'● Connected'}, disconnected:{bg:C.dangerBg,color:C.danger,label:'○ Disconnected'},
    banned:{bg:C.dangerBg,color:C.danger,label:'⛔ Banned'}, qr:{bg:C.warningBg,color:C.warning,label:'📷 Scan QR'} };
  const c = m[status]||m.disconnected;
  return <Badge label={c.label} color={c.color} bg={c.bg} />;
};
const Card = ({ children, style={} }) => (
  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, boxShadow:C.shadow, ...style }}>{children}</div>
);
const Btn = ({ children, onClick, variant='primary', size='md', disabled=false, style={} }) => {
  const v = { primary:{background:C.wa,color:'#fff',border:'none'},
    secondary:{background:'#F3F4F6',color:C.text,border:`1px solid ${C.border}`},
    danger:{background:C.dangerBg,color:C.danger,border:`1px solid ${C.danger}40`},
    ghost:{background:'transparent',color:C.textSub,border:`1px solid ${C.border}`},
    accent:{background:C.accentBg,color:C.accent,border:`1px solid ${C.accent}40`} }[variant];
  const s = { sm:{padding:'5px 12px',fontSize:12,borderRadius:7}, md:{padding:'8px 18px',fontSize:13,borderRadius:9},
    lg:{padding:'11px 24px',fontSize:14,borderRadius:10} }[size];
  return <button onClick={onClick} disabled={disabled} style={{ ...v,...s, cursor:disabled?'not-allowed':'pointer',
    fontFamily:F, fontWeight:600, opacity:disabled?0.5:1, display:'inline-flex', alignItems:'center',
    gap:6, whiteSpace:'nowrap', transition:'all 0.15s', ...style }}>{children}</button>;
};
const Input = ({ style={}, ...props }) => (
  <input {...props} style={{ width:'100%', background:'#F9FAFB', border:`1.5px solid ${C.border}`,
    borderRadius:9, padding:'9px 14px', color:C.text, fontSize:13, fontFamily:F,
    boxSizing:'border-box', outline:'none', ...style }}
    onFocus={e=>e.target.style.borderColor=C.wa} onBlur={e=>e.target.style.borderColor=C.border} />
);
const Sel = ({ children, style={}, ...props }) => (
  <select {...props} style={{ width:'100%', background:'#F9FAFB', border:`1.5px solid ${C.border}`,
    borderRadius:9, padding:'9px 14px', color:C.text, fontSize:13, fontFamily:F,
    boxSizing:'border-box', outline:'none', cursor:'pointer', ...style }}>{children}</select>
);
const Bar = ({ value=0, max=1, color=C.wa, h=6 }) => {
  const pct = Math.min(100,Math.round((value/Math.max(max,1))*100));
  return <div style={{ background:'#F1F3F7', borderRadius:h, height:h, overflow:'hidden' }}>
    <div style={{ width:`${pct}%`, background:color, height:'100%', borderRadius:h, transition:'width 0.5s' }} />
  </div>;
};
const Toggle = ({ val, onChange, size=20 }) => (
  <div onClick={()=>onChange(!val)} style={{ width:size*2, height:size, borderRadius:size,
    background:val?C.wa:C.border, cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
    <div style={{ width:size-4, height:size-4, background:'#fff', borderRadius:'50%', position:'absolute',
      top:2, left:val?size+2:2, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
  </div>
);
const Spinner = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:40 }}>
    <div style={{ width:28, height:28, border:`3px solid ${C.border}`, borderTopColor:C.wa,
      borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);
const Alert = ({ msg, type='error', onClose }) => (
  msg ? <div style={{ background:type==='error'?C.dangerBg:C.successBg,
    border:`1px solid ${type==='error'?C.danger:C.success}40`,
    color:type==='error'?C.danger:C.success, padding:'10px 16px', borderRadius:9,
    fontSize:13, marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
    <span>{msg}</span>
    <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, color:'inherit' }}>✕</button>
  </div> : null
);

// ─── LOGIN PAGE ───────────────────────────────────────────────
function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('vinay@grepix.com');
  const [password, setPassword] = useState('password');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setLoading(true); setErr('');
    try { await login(email, password); }
    catch(e) { setErr(e.response?.data?.error || 'Login failed'); }
    finally { setLoading(false); }
  };
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:C.bg, fontFamily:F }}>
      <Card style={{ width:380, padding:36 }}>
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <img src="/logo.png" alt="Grepix Infotech" style={{ width:90, height:90, objectFit:'contain', margin:'0 auto 10px', display:'block' }} />
          <h1 style={{ fontSize:20, fontWeight:800, color:'#2E7D32', letterSpacing:0.5 }}>G-WA</h1>
          <p style={{ color:C.muted, fontSize:12, marginTop:3 }}>Grepix Infotech · Campaign Automation</p>
        </div>
        <Alert msg={err} onClose={()=>setErr('')} />
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:12, color:C.muted, fontWeight:600, display:'block', marginBottom:6 }}>Email</label>
          <Input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" />
        </div>
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:12, color:C.muted, fontWeight:600, display:'block', marginBottom:6 }}>Password</label>
          <Input type="password" value={password} onChange={e=>setPassword(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&submit()} placeholder="••••••••" />
        </div>
        <Btn onClick={submit} disabled={loading} style={{ width:'100%', justifyContent:'center', fontSize:14, padding:'11px' }}>
          {loading ? 'Signing in...' : 'Sign In'}
        </Btn>
        <p style={{ textAlign:'center', fontSize:11, color:C.muted, marginTop:14 }}>Default: vinay@grepix.com / password</p>
      </Card>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────
const NAV = [
  {id:'dashboard', label:'Dashboard',    icon:'⊞'},
  {id:'accounts',  label:'WA Accounts',  icon:'📱'},
  {id:'campaigns', label:'Campaigns',    icon:'🚀'},
  {id:'inbox',     label:'Shared Inbox', icon:'💬'},
  {id:'leads',     label:'Leads & CRM',  icon:'👥'},
  {id:'analytics', label:'Analytics',    icon:'📊'},
  {id:'templates', label:'Templates',    icon:'📝'},
  {id:'settings',  label:'Settings',     icon:'⚙'},
];
function Sidebar({ active, onNav, user }) {
  return (
    <div style={{ width:230, background:C.surface, borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', flexShrink:0, boxShadow:'2px 0 8px rgba(0,0,0,0.04)' }}>
      <div style={{ padding:'16px 20px 14px', borderBottom:`1px solid ${C.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <img src="/logo.png" alt="Grepix" style={{ width:36, height:36, objectFit:'contain', flexShrink:0 }} />
          <div>
            <div style={{ fontWeight:800, fontSize:14, color:'#2E7D32' }}>G-WA</div>
            <div style={{ fontSize:10, color:C.muted }}>Campaign Platform</div>
          </div>
        </div>
      </div>
      <nav style={{ flex:1, padding:'10px', overflowY:'auto' }}>
        {NAV.map(item => (
          <button key={item.id} onClick={()=>onNav(item.id)} style={{
            width:'100%', display:'flex', alignItems:'center', gap:10, padding:'9px 12px',
            borderRadius:9, marginBottom:1, border:'none', cursor:'pointer', fontFamily:F,
            background:active===item.id?C.waLight:'transparent',
            color:active===item.id?C.waDark:C.textSub,
            fontSize:13, fontWeight:active===item.id?700:500,
            borderLeft:active===item.id?`3px solid ${C.wa}`:'3px solid transparent',
            transition:'all 0.13s' }}>
            <span style={{ fontSize:15, width:20, textAlign:'center' }}>{item.icon}</span>{item.label}
          </button>
        ))}
      </nav>
      <div style={{ padding:'14px 16px', borderTop:`1px solid ${C.border}`, background:'#FAFBFC' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <Av text={(user?.name||'?').split(' ').map(n=>n[0]).join('').slice(0,2)} size={34} bg={C.waLight} color={C.wa} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.name}</div>
            <div style={{ fontSize:10, color:C.muted, textTransform:'capitalize' }}>{user?.role?.replace('_',' ')}</div>
          </div>
          <div style={{ width:8, height:8, borderRadius:'50%', background:C.success }} />
        </div>
        <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${C.border}`, textAlign:'center' }}>
          <a href="https://www.grepixit.com" target="_blank" rel="noreferrer"
            style={{ fontSize:9, color:C.muted, textDecoration:'none', letterSpacing:0.2 }}>
            © {new Date().getFullYear()} Grepix Infotech Pvt Ltd
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────
function Dashboard({ onNav }) {
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([API.getDashboard(), API.getWeekly(), API.getCampaigns(), API.getAccounts()])
      .then(([d,w,c,a]) => {
        setData(d.data); setWeekly(w.data); setCampaigns(c.data.slice(0,5)); setAccounts(a.data);
      }).finally(()=>setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  const maxSent = Math.max(...weekly.map(d=>d.sent||0), 1);

  return (
    <div style={{ padding:28, overflowY:'auto', height:'100%' }}>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ fontSize:22, fontWeight:800, color:C.text, letterSpacing:-0.5 }}>Good morning 👋</h2>
        <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>Here's your WhatsApp campaign overview for today</p>
      </div>
      {/* Metrics */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:20 }}>
        {[
          {label:'Total Leads',        value:data?.total_leads?.toLocaleString()||0,     color:C.accent,  icon:'👥'},
          {label:'Messages Today',     value:data?.messages_today?.toLocaleString()||0,  color:C.wa,      icon:'📤'},
          {label:'Active Campaigns',   value:data?.active_campaigns||0,                  color:C.purple,  icon:'🚀'},
          {label:'Reply Rate',         value:(data?.reply_rate||0)+'%',                  color:C.success, icon:'💬'},
          {label:'Conversions',        value:data?.conversions||0,                       color:C.warning, icon:'🎯'},
          {label:'Connected Numbers',  value:data?.connected_numbers||0,                 color:C.success, icon:'📱'},
        ].map(m => (
          <Card key={m.label} style={{ padding:'20px 22px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontSize:12, color:C.muted, fontWeight:500 }}>{m.label}</span>
              <span style={{ fontSize:18 }}>{m.icon}</span>
            </div>
            <div style={{ fontSize:28, fontWeight:800, color:m.color, letterSpacing:-1 }}>{m.value}</div>
          </Card>
        ))}
      </div>
      {/* Chart + Accounts */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14, marginBottom:14 }}>
        <Card style={{ padding:22 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:18 }}>Messages This Week</h3>
          {weekly.length===0 ? <div style={{ color:C.muted, fontSize:13, textAlign:'center', padding:20 }}>No data yet — run a campaign!</div> : (
            <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:160 }}>
              {weekly.map((d,i) => (
                <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center' }}>
                  <div style={{ width:'100%', display:'flex', flexDirection:'column-reverse', gap:2, height:140 }}>
                    <div style={{ width:'100%', background:C.waLight, borderRadius:'4px 4px 0 0', height:`${((d.replies||0)/maxSent)*100}%`, minHeight:2 }} />
                    <div style={{ width:'100%', background:C.wa, borderRadius:'4px 4px 0 0', height:`${((d.sent||0)/maxSent)*100}%`, minHeight:4 }} />
                  </div>
                  <div style={{ fontSize:9, color:C.muted, marginTop:5 }}>
                    {new Date(d.stat_date).toLocaleDateString('en',{weekday:'short'})}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display:'flex', gap:16, marginTop:10 }}>
            {[[C.wa,'Sent'],[C.waLight,'Replies']].map(([c,l])=>(
              <div key={l} style={{ display:'flex', gap:5, alignItems:'center' }}>
                <div style={{ width:10, height:10, background:c, borderRadius:2 }} />
                <span style={{ fontSize:11, color:C.muted }}>{l}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card style={{ padding:22 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:C.text }}>Account Health</h3>
            <Btn variant="ghost" size="sm" onClick={()=>onNav('accounts')}>Manage</Btn>
          </div>
          {accounts.map(a => (
            <div key={a.id} style={{ display:'flex', gap:10, alignItems:'center', marginBottom:14 }}>
              <Av text={(a.name||'?').slice(0,2).toUpperCase()} size={34}
                bg={a.status==='connected'?C.waLight:C.dangerBg} color={a.status==='connected'?C.wa:C.danger} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:600, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.phone}</div>
                <Bar value={a.daily_sent} max={a.daily_limit} h={5} color={a.daily_sent/a.daily_limit>0.85?C.warning:C.wa} />
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:C.muted, marginTop:2 }}>
                  <span>{a.daily_sent}/{a.daily_limit}</span><span>{a.warmup_status}</span>
                </div>
              </div>
            </div>
          ))}
        </Card>
      </div>
      {/* Campaigns */}
      {campaigns.length>0 && (
        <Card style={{ padding:22 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:C.text }}>Active Campaigns</h3>
            <Btn variant="ghost" size="sm" onClick={()=>onNav('campaigns')}>View All →</Btn>
          </div>
          {campaigns.filter(c=>c.status==='active'||c.status==='paused').map(c => (
            <div key={c.id} style={{ marginBottom:16, paddingBottom:16, borderBottom:`1px solid ${C.border}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <span style={{ fontWeight:700, fontSize:13, color:C.text }}>{c.name}</span>
                <StatusBadge status={c.status} />
              </div>
              <div style={{ display:'flex', gap:16, fontSize:11, color:C.muted, marginBottom:8 }}>
                <span>📤 {(c.total_sent||0).toLocaleString()} sent</span>
                <span>💬 {c.total_replies||0} replies</span>
                <span>🎯 {c.total_conversions||0} won</span>
              </div>
              <Bar value={c.total_sent||0} max={c.total_leads||1} color={c.status==='active'?C.wa:C.warning} />
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ─── ACCOUNTS ────────────────────────────────────────────────
function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [qrModal, setQrModal] = useState(null); // { id, qr }
  const [form, setForm] = useState({ phone:'', name:'', daily_limit:300 });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name:'', daily_limit:300 });
  const qrPoll = useRef(null);

  const load = () => API.getAccounts().then(r=>setAccounts(r.data)).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  // socket: listen for QR + status updates
  useEffect(() => {
    const sock = getSocket();
    sock.on('qr', ({ accountId, qr }) => {
      setQrModal(prev => prev?.id===accountId ? { id:accountId, qr } : prev);
    });
    sock.on('account_status', ({ accountId, status }) => {
      setAccounts(prev => prev.map(a => a.id===accountId ? {...a, status} : a));
      if (status==='connected') { setQrModal(null); setMsg('✅ WhatsApp connected!'); load(); }
    });
    // Midnight auto-reset: refresh quota bars so daily_sent shows 0
    sock.on('daily_reset', () => { load(); setMsg('Daily message counts reset (midnight)'); });
    return () => { sock.off('qr'); sock.off('account_status'); sock.off('daily_reset'); };
  }, []);

  const addAccount = async () => {
    if (!form.phone) return setErr('Phone number required');
    try {
      await API.addAccount(form);
      setAddOpen(false);
      setForm({ phone:'', name:'', daily_limit:300 });
      load();
      setMsg('Account added. Click Connect to scan QR.');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const connect = async (acc) => {
    setErr('');
    // Join socket room for this account
    getSocket().emit('join_account', acc.id);
    setQrModal({ id:acc.id, qr:null, loading:true });
    try {
      await API.connectAccount(acc.id);
      // start polling as fallback
      qrPoll.current = setInterval(async () => {
        const r = await API.getQR(acc.id);
        if (r.data.status==='connected') { clearInterval(qrPoll.current); setQrModal(null); load(); return; }
        if (r.data.qr) setQrModal({ id:acc.id, qr:r.data.qr });
      }, 4000);
    } catch(e) { setQrModal(null); setErr('Failed to start connection'); }
  };

  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this WhatsApp number?')) return;
    await API.disconnectAccount(id);
    load();
  };

  const startEdit = (acc) => {
    setEditingId(acc.id);
    setEditForm({ name: acc.name || '', daily_limit: acc.daily_limit });
  };

  const saveEdit = async () => {
    try {
      await API.updateAccount(editingId, editForm);
      setEditingId(null);
      load(); setMsg('Account updated!');
    } catch(e) { setErr(e.response?.data?.error || 'Failed to update'); }
  };

  useEffect(() => () => clearInterval(qrPoll.current), []);

  if (loading) return <Spinner />;
  return (
    <div style={{ padding:28, overflowY:'auto', height:'100%' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:C.text }}>WhatsApp Accounts</h2>
          <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>Connect numbers via QR scan · Manage warmup and daily limits</p>
        </div>
        <Btn onClick={()=>setAddOpen(true)}>+ Add Number</Btn>
      </div>

      <Alert msg={err} onClose={()=>setErr('')} />
      <Alert msg={msg} type="success" onClose={()=>setMsg('')} />

      {/* Add form */}
      {addOpen && (
        <Card style={{ padding:22, marginBottom:20, border:`2px solid ${C.wa}30` }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>Add WhatsApp Number</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto', gap:12, alignItems:'end' }}>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Phone Number *</label>
              <Input placeholder="+91 98765 43210" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Label / Name</label>
              <Input placeholder="e.g. Grepix Sales 1" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Daily Limit</label>
              <Input type="number" value={form.daily_limit} onChange={e=>setForm(f=>({...f,daily_limit:+e.target.value}))} />
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn onClick={addAccount}>Add</Btn>
              <Btn variant="ghost" onClick={()=>setAddOpen(false)}>Cancel</Btn>
            </div>
          </div>
        </Card>
      )}

      {/* QR Modal */}
      {qrModal && (
        <Card style={{ padding:28, marginBottom:20, display:'flex', gap:32, alignItems:'center', border:`2px solid ${C.wa}30` }}>
          <div style={{ textAlign:'center', flexShrink:0 }}>
            {qrModal.qr ? (
              <img src={qrModal.qr} alt="QR Code" style={{ width:200, height:200, borderRadius:12, border:`2px solid ${C.border}` }} />
            ) : (
              <div style={{ width:200, height:200, background:'#F9FAFB', borderRadius:12, border:`2px dashed ${C.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:10 }}>
                <Spinner />
                <div style={{ fontSize:12, color:C.muted }}>Generating QR...</div>
              </div>
            )}
            <div style={{ fontSize:11, color:C.muted, marginTop:8 }}>QR refreshes every 60s</div>
          </div>
          <div style={{ flex:1 }}>
            <h3 style={{ fontSize:17, fontWeight:800, color:C.text, marginBottom:8 }}>Scan QR to Connect WhatsApp</h3>
            <p style={{ fontSize:13, color:C.muted, lineHeight:1.6, marginBottom:14 }}>
              Open WhatsApp on your mobile phone, go to <strong>Menu → Linked Devices → Link a Device</strong>, then scan this QR code.
            </p>
            <ol style={{ margin:0, padding:'0 0 0 18px', color:C.textSub, fontSize:13, lineHeight:2.2 }}>
              <li>Open WhatsApp on your phone</li>
              <li>Tap ⋮ Menu → Linked Devices</li>
              <li>Tap "Link a Device"</li>
              <li>Point camera at the QR above</li>
            </ol>
            <div style={{ marginTop:18, display:'flex', gap:10 }}>
              <Btn variant="ghost" onClick={()=>{ clearInterval(qrPoll.current); setQrModal(null); }}>Cancel</Btn>
              <Btn variant="secondary" onClick={()=>connect(accounts.find(a=>a.id===qrModal.id)||{id:qrModal.id})}>🔄 Restart</Btn>
            </div>
          </div>
        </Card>
      )}

      {/* Account cards */}
      <div style={{ display:'grid', gap:14 }}>
        {accounts.length===0 && (
          <Card style={{ padding:40, textAlign:'center' }}>
            <div style={{ fontSize:36, marginBottom:12 }}>📱</div>
            <div style={{ fontSize:15, fontWeight:700, color:C.textSub }}>No WhatsApp numbers added yet</div>
            <div style={{ fontSize:13, color:C.muted, marginBottom:16 }}>Add a number and scan QR to connect</div>
            <Btn onClick={()=>setAddOpen(true)}>+ Add Number</Btn>
          </Card>
        )}
        {accounts.map(acc => (
          <Card key={acc.id} style={{ padding:22, border:acc.status==='connected'?`1px solid ${C.wa}30`:editingId===acc.id?`2px solid ${C.accent}40`:undefined }}>
            <div style={{ display:'flex', gap:16, alignItems:'center' }}>
              <Av text={(acc.name||'?').slice(0,2).toUpperCase()} size={50}
                bg={acc.status==='connected'?C.waLight:acc.status==='banned'?C.dangerBg:'#F9FAFB'}
                color={acc.status==='connected'?C.wa:acc.status==='banned'?C.danger:C.muted} />
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:4 }}>
                  <span style={{ fontWeight:800, fontSize:16, color:C.text }}>{acc.phone}</span>
                  <StatusBadge status={acc.status} />
                  <Badge label={acc.warmup_status} color={acc.warmup_status==='mature'?C.success:acc.warmup_status==='warm'?C.warning:C.muted}
                    bg={acc.warmup_status==='mature'?C.successBg:acc.warmup_status==='warm'?C.warningBg:'#F3F4F6'} />
                </div>

                {editingId === acc.id ? (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                    <div>
                      <label style={{ fontSize:10, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Label / Name</label>
                      <Input value={editForm.name} placeholder="e.g. Sales Number 1"
                        onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} />
                    </div>
                    <div>
                      <label style={{ fontSize:10, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Daily Message Quota</label>
                      <Input type="number" value={editForm.daily_limit} min={1}
                        onChange={e=>setEditForm(f=>({...f,daily_limit:+e.target.value}))} />
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize:12, color:C.muted, marginBottom:10 }}>{acc.name} · {acc.campaign_count||0} campaigns</div>
                )}

                <div style={{ display:'grid', gridTemplateColumns:'220px auto', gap:20, alignItems:'center' }}>
                  <div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:C.muted, marginBottom:5 }}>
                      <span>Daily Messages</span>
                      <span style={{ color:C.text, fontWeight:600 }}>
                        {acc.daily_sent}/{editingId===acc.id ? editForm.daily_limit : acc.daily_limit}
                      </span>
                    </div>
                    <Bar value={acc.daily_sent} max={editingId===acc.id ? editForm.daily_limit : acc.daily_limit} h={7}
                      color={acc.daily_sent/(editingId===acc.id?editForm.daily_limit:acc.daily_limit)>0.85?C.warning:C.wa} />
                  </div>
                </div>
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:8, flexShrink:0, alignItems:'flex-end' }}>
                {editingId === acc.id ? (
                  <>
                    <Btn size="sm" onClick={saveEdit}>✓ Save</Btn>
                    <Btn variant="ghost" size="sm" onClick={()=>setEditingId(null)}>✕ Cancel</Btn>
                  </>
                ) : (
                  <>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      {acc.is_primary ? (
                        <span style={{ fontSize:11, fontWeight:700, color:C.wa, background:C.waLight, border:`1px solid ${C.wa}50`, borderRadius:8, padding:'4px 10px' }}>★ Primary</span>
                      ) : (
                        <Btn variant="ghost" size="sm" onClick={()=>API.setPrimaryAccount(acc.id).then(load)} title="Set as primary number for starting new chats">☆ Set Primary</Btn>
                      )}
                      {acc.status!=='connected' ? (
                        <Btn onClick={()=>connect(acc)} style={{ background:C.wa, color:'#fff', border:'none' }}>
                          📷 Connect & Scan QR
                        </Btn>
                      ) : (
                        <Btn variant="danger" onClick={()=>disconnect(acc.id)}>Disconnect</Btn>
                      )}
                      <Btn variant="ghost" size="sm" onClick={()=>startEdit(acc)} title="Edit label and quota">✏️</Btn>
                      <Btn variant="ghost" size="sm" onClick={()=>{ if(window.confirm('Delete this account?')) API.deleteAccount(acc.id).then(load); }}>🗑</Btn>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── CAMPAIGNS ───────────────────────────────────────────────
function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [leads, setLeads]= useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [enrollModal, setEnrollModal] = useState(null); // campaign id
  const [selectedLeads, setSelectedLeads] = useState([]);
  // templatePicker: null = closed, { seqIndex, variantIndex } = open (variantIndex null = main msg)
  const [templatePicker, setTemplatePicker] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [csvLeads, setCsvLeads] = useState([]);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [allLeads, setAllLeads] = useState([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadSearch, setLeadSearch] = useState('');
  const [leadTagFilter, setLeadTagFilter] = useState('');
  const [leadStageFilter, setLeadStageFilter] = useState('');
  const [leadIdFilter, setLeadIdFilter] = useState('');
  const [leadDateFrom, setLeadDateFrom] = useState('');
  const [leadDateTo, setLeadDateTo] = useState('');
  const [leadTags, setLeadTags] = useState([]);
  const [pickedLeads, setPickedLeads] = useState([]);

  const LEAD_STAGES = ['New','Hot','Warm','Cold','Budget','General','Closed-Won','Lost'];

  const loadTemplates = () => API.getTemplates().then(r => setTemplates(r.data)).catch(() => {});
  const loadAllLeads  = (extra = {}) => {
    const p = { limit: 200 };
    if (leadSearch)      p.search    = leadSearch;
    if (leadTagFilter)   p.tag       = leadTagFilter;
    if (leadStageFilter) p.stage     = leadStageFilter;
    if (leadIdFilter)    p.id        = leadIdFilter;
    if (leadDateFrom)    p.date_from = leadDateFrom;
    if (leadDateTo)      p.date_to   = leadDateTo;
    Object.assign(p, extra);
    return API.getLeads(p).then(r => { setAllLeads(r.data.leads || []); setLeadTotal(r.data.total || 0); }).catch(() => {});
  };
  const selectAllFiltered = async () => {
    const p = { limit: 5000 };
    if (leadSearch)      p.search    = leadSearch;
    if (leadTagFilter)   p.tag       = leadTagFilter;
    if (leadStageFilter) p.stage     = leadStageFilter;
    if (leadIdFilter)    p.id        = leadIdFilter;
    if (leadDateFrom)    p.date_from = leadDateFrom;
    if (leadDateTo)      p.date_to   = leadDateTo;
    const r = await API.getLeads(p).catch(() => null);
    if (r) setPickedLeads((r.data.leads || []).map(l => l.id));
  };

  const [form, setForm] = useState({
    name:'', description:'', delay_mode:'balanced', delay_min:15, delay_max:90,
    working_hours:1, lunch_break:1, numbers:[], tags:'',
    sequences:[{ content:'Hello {{name}}, thank you for your interest in our services! I am reaching out from Grepix Infotech.', delay_days:0, variants:[] }]
  });

  const load = () => Promise.all([API.getCampaigns(), API.getAccounts()])
    .then(([c,a])=>{ setCampaigns(c.data); setAccounts(a.data); }).finally(()=>setLoading(false));

  useEffect(() => {
    load(); loadTemplates();
    API.getLeadTags().then(r => setLeadTags(r.data || [])).catch(() => {});
    loadAllLeads();
  }, []);
  useEffect(() => { loadAllLeads(); }, [leadSearch, leadTagFilter, leadStageFilter, leadIdFilter, leadDateFrom, leadDateTo]);

  const toggleStatus = async (id, current) => {
    const next = current==='active'?'paused':current==='paused'?'active':'active';
    try { await API.setCampaignStatus(id, next); load(); setMsg(`Campaign ${next}`); }
    catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const startEditCampaign = async (id) => {
    try {
      const r = await API.getCampaign(id);
      const c = r.data;
      setForm({
        name: c.name, description: c.description || '',
        delay_mode: c.delay_mode, delay_min: c.delay_min, delay_max: c.delay_max,
        working_hours: c.working_hours, lunch_break: c.lunch_break,
        numbers: (c.numbers||[]).map(n => n.id),
        tags: (c.tags||[]).join(', '),
        sequences: (c.sequences||[]).map(s => ({
          content: s.content, delay_days: s.delay_days,
          variants: Array.isArray(s.variants) ? s.variants : (s.variants ? JSON.parse(s.variants) : []),
          media_url: s.media_url || ''
        }))
      });
      setEditingCampaignId(id);
      setPickedLeads([]); setCsvLeads([]);
      setCreating(true);
    } catch(e) { setErr('Failed to load campaign'); }
  };

  const doDuplicateCampaign = async (id) => {
    try {
      await API.duplicateCampaign(id);
      load();
      setMsg('Campaign duplicated as draft.');
    } catch(e) { setErr(e.response?.data?.error || 'Duplicate failed'); }
  };

  const handleCampaignCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (results) => {
        const mapped = results.data
          .map(r => ({ name:r.name||r.Name, phone:r.phone||r.Phone||r.mobile||r.Mobile, city:r.city||r.City||'', company:r.company||r.Company||'' }))
          .filter(r => r.name && r.phone);
        if (!mapped.length) return setErr('No valid rows found. CSV needs columns: name, phone');
        setCsvLeads(mapped);
      }
    });
    e.target.value = '';
  };

  const launchCampaign = async () => {
    setErr('');
    if (!form.name) return setErr('Campaign name required');
    if (!form.sequences.length || !form.sequences[0].content) return setErr('At least one message required');
    if (!form.numbers.length) return setErr('Select at least one WhatsApp number');
    const hasEmptyVariant = form.sequences.some(s => (s.variants||[]).some(v => !v || !v.trim()));
    if (hasEmptyVariant) return setErr('One or more variant message boxes are empty. Fill them in or click ✕ to remove them.');
    try {
      const tags = form.tags.split(',').map(t=>t.trim()).filter(Boolean);
      const payload = { ...form, tags, numbers: form.numbers.map(Number) };
      let campaignId;

      if (editingCampaignId) {
        await API.updateCampaign(editingCampaignId, payload);
        campaignId = editingCampaignId;
        setMsg('Campaign updated.');
      } else {
        const r = await API.createCampaign(payload);
        campaignId = r.data.id;
        setMsg('Campaign created! Use Enroll Leads to start sending.');
      }

      // Enroll CSV leads
      if (csvLeads.length) {
        const imp = await API.bulkImport(csvLeads, campaignId);
        await API.setCampaignStatus(campaignId, 'active');
        setMsg(`Campaign ${editingCampaignId?'updated':'created'} & activated! ${imp.data.imported} leads enrolled · ${imp.data.skipped} skipped.`);
      }

      // Enroll hand-picked leads
      if (pickedLeads.length) {
        await API.enrollLeads(campaignId, pickedLeads);
        await API.setCampaignStatus(campaignId, 'active');
        setMsg(`Campaign ${editingCampaignId?'updated':'created'} & activated! ${pickedLeads.length} leads enrolled.`);
      }

      setCreating(false); setEditingCampaignId(null);
      setCsvLeads([]); setPickedLeads([]);
      setForm({ name:'', description:'', delay_mode:'balanced', delay_min:15, delay_max:90,
        working_hours:1, lunch_break:1, numbers:[], tags:'',
        sequences:[{ content:'Hello {{name}}, thank you for your interest!', delay_days:0, variants:[] }] });
      load();
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const openEnroll = async (campaignId) => {
    const r = await API.getLeads({ limit:200 });
    setLeads(r.data.leads); setEnrollModal(campaignId); setSelectedLeads([]);
  };

  const doEnroll = async () => {
    if (!selectedLeads.length) return;
    await API.enrollLeads(enrollModal, selectedLeads);
    await API.setCampaignStatus(enrollModal, 'active');
    setEnrollModal(null); load();
    setMsg(`${selectedLeads.length} leads enrolled and campaign activated!`);
  };

  const filtered = filter==='all' ? campaigns : campaigns.filter(c=>c.status===filter);

  if (loading) return <Spinner />;

  if (creating) return (
    <div style={{ padding:28, overflowY:'auto', height:'100%' }}>
      <div style={{ display:'flex', gap:12, alignItems:'center', marginBottom:24 }}>
        <Btn variant="ghost" size="sm" onClick={()=>{ setCreating(false); setEditingCampaignId(null); setPickedLeads([]); setCsvLeads([]); }}>← Back</Btn>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:C.text }}>{editingCampaignId ? '✏️ Edit Campaign' : 'Create Campaign'}</h2>
          <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>{editingCampaignId ? 'Update campaign details, messages, and settings' : 'Set up automated WhatsApp message sequences'}</p>
        </div>
      </div>
      <Alert msg={err} onClose={()=>setErr('')} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <Card style={{ padding:22 }}>
            <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:18 }}>Campaign Details</h3>
            {[['Campaign Name *','name','e.g. HireMe Q3 Outreach','text'],['Description','description','What this campaign is about','text']].map(([l,k,ph,t])=>(
              <div key={k} style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, color:C.muted, fontWeight:600, display:'block', marginBottom:6 }}>{l}</label>
                <Input type={t} placeholder={ph} value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} />
              </div>
            ))}
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, color:C.muted, fontWeight:600, display:'block', marginBottom:6 }}>Tags (comma separated)</label>
              <Input placeholder="taxi, fleet, b2b" value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, color:C.muted, fontWeight:600, display:'block', marginBottom:8 }}>Anti-Ban Delay Mode</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[['conservative','Conservative','30-180s'],['balanced','Balanced','15-90s'],['aggressive','Aggressive','5-30s'],['custom','Custom','Manual']].map(([k,l,sub])=>(
                  <button key={k} onClick={()=>setForm(f=>({...f,delay_mode:k}))} style={{
                    padding:10, borderRadius:9, cursor:'pointer', fontFamily:F, textAlign:'center',
                    border:`1.5px solid ${form.delay_mode===k?C.wa:C.border}`,
                    background:form.delay_mode===k?C.waLight:'#F9FAFB',
                    color:form.delay_mode===k?C.waDark:C.textSub }}>
                    <div style={{ fontWeight:700, fontSize:12 }}>{l}</div>
                    <div style={{ fontSize:10, opacity:0.7 }}>{sub}</div>
                  </button>
                ))}
              </div>
            </div>
            {form.delay_mode==='custom' && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
                <div>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Min Delay (sec)</label>
                  <Input type="number" value={form.delay_min} onChange={e=>setForm(f=>({...f,delay_min:+e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Max Delay (sec)</label>
                  <Input type="number" value={form.delay_max} onChange={e=>setForm(f=>({...f,delay_max:+e.target.value}))} />
                </div>
              </div>
            )}
            <div>
              <label style={{ fontSize:12, color:C.muted, fontWeight:600, display:'block', marginBottom:8 }}>
                Assign WhatsApp Numbers * <span style={{ color:C.danger }}>(select at least one connected number)</span>
              </label>
              {accounts.filter(a=>a.status==='connected').length===0 && (
                <div style={{ background:C.warningBg, border:`1px solid ${C.warning}40`, borderRadius:9, padding:'10px 14px', fontSize:12, color:C.warning }}>
                  ⚠️ No connected numbers. Go to WA Accounts and connect a number first.
                </div>
              )}
              {accounts.filter(a=>a.status==='connected').map(acc=>(
                <label key={acc.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, cursor:'pointer', padding:'8px 10px', borderRadius:8, border:`1px solid ${form.numbers.includes(acc.id)?C.wa:C.border}`, background:form.numbers.includes(acc.id)?C.waLight:'#FAFBFC' }}>
                  <input type="checkbox" checked={form.numbers.includes(acc.id)} style={{ accentColor:C.wa }}
                    onChange={()=>setForm(f=>({ ...f, numbers:f.numbers.includes(acc.id)?f.numbers.filter(n=>n!==acc.id):[...f.numbers,acc.id] }))} />
                  <Av text={(acc.name||'?').slice(0,2).toUpperCase()} size={28} bg={C.waLight} color={C.wa} />
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{acc.phone}</div>
                    <div style={{ fontSize:10, color:C.muted }}>{acc.daily_limit-acc.daily_sent} slots free today</div>
                  </div>
                </label>
              ))}
            </div>
          </Card>
        </div>
        <div>
          {/* Template Picker Modal */}
          {templatePicker !== null && (
            <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000,
              display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Card style={{ width:580, maxHeight:'80vh', display:'flex', flexDirection:'column', borderRadius:16 }}>
                <div style={{ padding:'18px 22px', borderBottom:`1px solid ${C.border}`,
                  display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
                  <div>
                    <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:C.text }}>📝 Choose a Template</h3>
                    <p style={{ margin:'3px 0 0', fontSize:12, color:C.muted }}>Select a saved template for step {(templatePicker?.seqIndex??0) + 1}{templatePicker?.variantIndex != null ? ` · Variant ${templatePicker.variantIndex + 2}` : ''}</p>
                  </div>
                  <button onClick={()=>{ setTemplatePicker(null); setTemplateSearch(''); }}
                    style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.muted }}>×</button>
                </div>
                <div style={{ padding:'12px 22px', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
                  <Input placeholder="Search templates by name or category..."
                    value={templateSearch} onChange={e=>setTemplateSearch(e.target.value)} />
                </div>
                <div style={{ flex:1, overflowY:'auto', padding:'12px 22px' }}>
                  {templates.length === 0 && (
                    <div style={{ textAlign:'center', padding:30, color:C.muted }}>
                      <div style={{ fontSize:32, marginBottom:8 }}>📝</div>
                      <div style={{ fontSize:14, fontWeight:600, color:C.textSub }}>No templates yet</div>
                      <div style={{ fontSize:12, marginTop:4 }}>Go to Templates page to create reusable messages</div>
                    </div>
                  )}
                  {templates
                    .filter(t => !templateSearch ||
                      t.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
                      (t.category||'').toLowerCase().includes(templateSearch.toLowerCase()) ||
                      t.content.toLowerCase().includes(templateSearch.toLowerCase()))
                    .map(t => (
                      <div key={t.id} style={{ background:'#FAFBFC', border:`1.5px solid ${C.border}`,
                        borderRadius:10, padding:'14px 16px', marginBottom:10, cursor:'pointer' }}
                        onMouseEnter={e=>{ e.currentTarget.style.border=`1.5px solid ${C.wa}`; e.currentTarget.style.background=C.waLight; }}
                        onMouseLeave={e=>{ e.currentTarget.style.border=`1.5px solid ${C.border}`; e.currentTarget.style.background='#FAFBFC'; }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                          <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{t.name}</span>
                          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                            <Badge label={t.category||'Generic'} color={C.accent} bg={C.accentBg} />
                            <Btn size="sm" onClick={()=>{
                              setForm(f=>({...f, sequences:f.sequences.map((sq,j)=>j===templatePicker.seqIndex
                                ? (templatePicker.variantIndex==null
                                    ? {...sq,content:t.content}
                                    : {...sq,variants:sq.variants.map((v,k)=>k===templatePicker.variantIndex?t.content:v)})
                                : sq)}));
                              setTemplatePicker(null); setTemplateSearch('');
                            }}>Use This</Btn>
                          </div>
                        </div>
                        <div style={{ fontSize:12, color:C.textSub, lineHeight:1.6, background:'#fff',
                          borderRadius:8, padding:'8px 12px', border:`1px solid ${C.border}`, marginBottom:8 }}>
                          {t.content}
                        </div>
                        {(t.variables||[]).length > 0 && (
                          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                            {(t.variables||[]).map(v=>(
                              <code key={v} style={{ fontSize:10, background:C.accentBg, color:C.accent, padding:'2px 6px', borderRadius:4 }}>{`{{${v}}}`}</code>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
                <div style={{ padding:'12px 22px', borderTop:`1px solid ${C.border}`,
                  display:'flex', justifyContent:'space-between', alignItems:'center',
                  background:'#FAFBFC', flexShrink:0, borderRadius:'0 0 16px 16px' }}>
                  <span style={{ fontSize:12, color:C.muted }}>{templates.length} template{templates.length!==1?'s':''} saved</span>
                  <Btn variant="ghost" size="sm" onClick={()=>{ setTemplatePicker(null); setTemplateSearch(''); }}>Close</Btn>
                </div>
              </Card>
            </div>
          )}

          {/* Message Sequence Card */}
          <Card style={{ padding:22 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
              <h3 style={{ fontSize:14, fontWeight:700, color:C.text }}>Message Sequence</h3>
              <Badge label={`${form.sequences.length} message${form.sequences.length!==1?'s':''}`} color={C.accent} bg={C.accentBg} />
            </div>
            {form.sequences.map((s,i)=>(
              <div key={i} style={{ background:'#F9FAFB', border:`1.5px solid ${C.border}`, borderRadius:10, padding:14, marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <div style={{ width:22, height:22, background:C.wa, borderRadius:6, fontSize:11, fontWeight:800, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>{i+1}</div>
                    <span style={{ fontSize:12, color:C.muted, fontWeight:600 }}>
                      {i===0?'Day 0 — Initial Message':`Day ${s.delay_days||0} — Follow-up ${i}`}
                    </span>
                  </div>
                  <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                    <button onClick={()=>{ setTemplatePicker({ seqIndex:i, variantIndex:null }); setTemplateSearch(''); }}
                      style={{ background:C.accentBg, border:`1px solid ${C.accent}30`, color:C.accent,
                        borderRadius:7, padding:'4px 10px', fontSize:11, fontWeight:600,
                        cursor:'pointer', fontFamily:F, display:'flex', alignItems:'center', gap:4 }}>
                      📝 Use Template
                    </button>
                    {i>0 && <button onClick={()=>setForm(f=>({...f,sequences:f.sequences.filter((_,j)=>j!==i)}))}
                      style={{ background:'none', border:'none', color:C.danger, cursor:'pointer', fontSize:12, fontWeight:600 }}>✕</button>}
                  </div>
                </div>
                {i>0 && (
                  <div style={{ marginBottom:8 }}>
                    <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Send after (days)</label>
                    <Input type="number" value={s.delay_days||0} style={{ width:80 }}
                      onChange={e=>setForm(f=>({...f,sequences:f.sequences.map((sq,j)=>j===i?{...sq,delay_days:+e.target.value}:sq)}))} />
                  </div>
                )}
                <textarea value={s.content} rows={4}
                  onChange={e=>setForm(f=>({...f,sequences:f.sequences.map((sq,j)=>j===i?{...sq,content:e.target.value}:sq)}))}
                  placeholder={i===0?"Write your opening message or click 'Use Template'...":"Write your follow-up message..."}
                  style={{ width:'100%', background:'#fff', border:`1px solid ${C.border}`, borderRadius:8,
                    padding:'9px 12px', color:C.text, fontSize:12, fontFamily:F, resize:'vertical', boxSizing:'border-box', minHeight:80 }} />
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:6 }}>
                  <div style={{ fontSize:10, color:C.muted }}>
                    Variables:{' '}
                    {['name','city','company'].map(v=>(
                      <code key={v}
                        onClick={()=>setForm(f=>({...f,sequences:f.sequences.map((sq,j)=>j===i?{...sq,content:sq.content+`{{${v}}}`}:sq)}))}
                        style={{ background:'#F3F4F6', padding:'1px 5px', borderRadius:3, marginRight:4,
                          cursor:'pointer', fontSize:10, border:`1px solid ${C.border}` }}
                        title="Click to insert">
                        {`{{${v}}}`}
                      </code>
                    ))}
                  </div>
                  <span style={{ fontSize:10, color:C.muted }}>{s.content?.length||0} chars</span>
                </div>
                {/* A/B Variants — sent randomly in place of the main message */}
                {(s.variants||[]).map((v,vi)=>(
                  <div key={vi} style={{ marginTop:8, background:'#fff', border:`1.5px dashed ${C.wa}60`, borderRadius:8, padding:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:C.wa }}>Variant {vi+2} — sent randomly</span>
                      <div style={{ display:'flex', gap:4 }}>
                        <button onClick={()=>{ setTemplatePicker({ seqIndex:i, variantIndex:vi }); setTemplateSearch(''); }}
                          style={{ background:'transparent', border:`1px solid ${C.accent}30`, color:C.accent, borderRadius:5, padding:'2px 7px', fontSize:10, cursor:'pointer', fontFamily:F }}>
                          📝 Template
                        </button>
                        <button onClick={()=>setForm(f=>({...f,sequences:f.sequences.map((sq,j)=>j===i?{...sq,variants:sq.variants.filter((_,k)=>k!==vi)}:sq)}))}
                          style={{ background:'none', border:'none', color:C.danger, cursor:'pointer', fontSize:12, fontWeight:700 }}>✕</button>
                      </div>
                    </div>
                    <textarea value={v} rows={3}
                      onChange={e=>setForm(f=>({...f,sequences:f.sequences.map((sq,j)=>j===i?{...sq,variants:sq.variants.map((vx,k)=>k===vi?e.target.value:vx)}:sq)}))}
                      placeholder="Alternative message — one of these is chosen randomly per lead..."
                      style={{ width:'100%', background:'#F9FAFB', border:`1px solid ${C.border}`, borderRadius:7,
                        padding:'8px 11px', color:C.text, fontSize:12, fontFamily:F, resize:'vertical', boxSizing:'border-box', minHeight:66 }} />
                  </div>
                ))}
                <button onClick={()=>setForm(f=>({...f,sequences:f.sequences.map((sq,j)=>j===i?{...sq,variants:[...(sq.variants||[]),'']}:sq)}))}
                  style={{ width:'100%', background:'transparent', border:`1px dashed ${C.wa}50`, borderRadius:7,
                    padding:'5px', color:C.wa, cursor:'pointer', fontFamily:F, fontSize:11, fontWeight:600, marginTop:6,
                    display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                  + Add Message Variant (A/B Random)
                </button>
              </div>
            ))}
            <button onClick={()=>setForm(f=>({...f,sequences:[...f.sequences,{content:'',delay_days:3,variants:[]}]}))}
              style={{ width:'100%', background:'transparent', border:`1.5px dashed ${C.border}`, borderRadius:10,
                padding:11, color:C.muted, cursor:'pointer', fontFamily:F, fontSize:13, fontWeight:600, marginBottom:16,
                display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              + Add Follow-up Message
            </button>
            <Btn onClick={launchCampaign} style={{ width:'100%', justifyContent:'center' }}>{editingCampaignId ? '✏️ Update Campaign' : '💾 Save Campaign'}</Btn>
          </Card>
        </div>
      </div>

      {/* Lead Picker — select from existing leads */}
      <Card style={{ padding:22, marginTop:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div>
            <h3 style={{ fontSize:14, fontWeight:700, color:C.text, margin:0 }}>👥 Select Leads <span style={{ fontSize:11, fontWeight:500, color:C.muted }}>(optional)</span></h3>
            <p style={{ fontSize:12, color:C.muted, marginTop:4, marginBottom:0 }}>
              Filter and pick leads from your Perfex CRM to enroll when the campaign is saved.
              {leadTotal > 0 && <span style={{ color:C.accent, fontWeight:600 }}> {leadTotal.toLocaleString()} leads match current filters.</span>}
            </p>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {pickedLeads.length > 0 && <Badge label={`${pickedLeads.length} selected`} color={C.wa} bg={C.waLight} />}
          </div>
        </div>

        {/* Filter bar */}
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:12 }}>
          <div style={{ display:'flex', gap:8 }}>
            <Input placeholder="🔍 Search name / phone..." value={leadSearch}
              onChange={e => setLeadSearch(e.target.value)} style={{ flex:2 }} />
            <input type="number" placeholder="Lead ID" value={leadIdFilter}
              onChange={e => setLeadIdFilter(e.target.value)}
              style={{ width:90, border:`1px solid ${C.border}`, borderRadius:8, padding:'7px 10px', fontSize:12, fontFamily:F, color:C.text }} />
            <Sel value={leadTagFilter} onChange={e => setLeadTagFilter(e.target.value)} style={{ flex:1 }}>
              <option value="">All Tags</option>
              {leadTags.map(t => <option key={t.name} value={t.name}>{t.name} ({t.count})</option>)}
            </Sel>
            <Sel value={leadStageFilter} onChange={e => setLeadStageFilter(e.target.value)} style={{ flex:1 }}>
              <option value="">All Stages</option>
              {LEAD_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </Sel>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <label style={{ fontSize:11, color:C.muted, fontWeight:600, whiteSpace:'nowrap' }}>Date Added:</label>
            <input type="date" value={leadDateFrom} onChange={e => setLeadDateFrom(e.target.value)}
              style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'5px 10px', fontSize:12, fontFamily:F, color:C.text }} />
            <span style={{ color:C.muted, fontSize:12 }}>to</span>
            <input type="date" value={leadDateTo} onChange={e => setLeadDateTo(e.target.value)}
              style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'5px 10px', fontSize:12, fontFamily:F, color:C.text }} />
            {(leadSearch||leadTagFilter||leadStageFilter||leadIdFilter||leadDateFrom||leadDateTo) && (
              <button onClick={()=>{ setLeadSearch(''); setLeadTagFilter(''); setLeadStageFilter(''); setLeadIdFilter(''); setLeadDateFrom(''); setLeadDateTo(''); }}
                style={{ background:'none', border:`1px solid ${C.border}`, borderRadius:7, padding:'4px 10px', fontSize:11, color:C.muted, cursor:'pointer', fontFamily:F }}>
                ✕ Clear
              </button>
            )}
            <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
              <button onClick={selectAllFiltered}
                style={{ background:C.accent, color:'#fff', border:'none', borderRadius:7, padding:'5px 12px', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:F }}>
                Select all {leadTotal > 0 ? leadTotal.toLocaleString() : ''} matching
              </button>
              {pickedLeads.length > 0 && (
                <button onClick={() => setPickedLeads([])}
                  style={{ background:'none', border:`1px solid ${C.danger}40`, color:C.danger, borderRadius:7, padding:'5px 12px', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:F }}>
                  Clear selection
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Lead list */}
        <div style={{ maxHeight:240, overflowY:'auto', border:`1px solid ${C.border}`, borderRadius:9 }}>
          {allLeads.map(l => (
            <label key={l.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px',
              borderBottom:`1px solid ${C.border}`, cursor:'pointer',
              background: pickedLeads.includes(l.id) ? C.waLight : '#fff' }}>
              <input type="checkbox" checked={pickedLeads.includes(l.id)}
                style={{ accentColor:C.wa }}
                onChange={() => setPickedLeads(prev =>
                  prev.includes(l.id) ? prev.filter(id => id !== l.id) : [...prev, l.id]
                )} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:600, color:C.text }}>
                  <span style={{ color:C.muted, fontSize:10, marginRight:6 }}>#{l.id}</span>{l.name}
                </div>
                <div style={{ fontSize:11, color:C.muted }}>{l.phone}{l.company ? ` · ${l.company}` : ''}</div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3 }}>
                <Badge label={l.pipeline_stage||'New'} color={C.accent} bg={C.accentBg} />
                {(l.tags||[]).slice(0,2).map(t => (
                  <span key={t} style={{ fontSize:9, background:'#EEF2FF', color:'#4F46E5', borderRadius:4, padding:'1px 5px', fontWeight:600 }}>{t}</span>
                ))}
              </div>
            </label>
          ))}
          {allLeads.length === 0 && (
            <div style={{ padding:'20px 14px', textAlign:'center', color:C.muted, fontSize:12 }}>No leads match the current filters.</div>
          )}
        </div>
        {leadTotal > allLeads.length && (
          <div style={{ marginTop:6, fontSize:11, color:C.muted, textAlign:'center' }}>
            Showing {allLeads.length} of {leadTotal.toLocaleString()} · Use filters to narrow down or "Select all matching" to enroll all.
          </div>
        )}
      </Card>

      {/* Lead Import — full-width card below the 2-column grid */}
      <Card style={{ padding:22, marginTop:20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
          <div>
            <h3 style={{ fontSize:14, fontWeight:700, color:C.text, margin:0 }}>📋 Lead Import <span style={{ fontSize:11, fontWeight:500, color:C.muted }}>(optional)</span></h3>
            <p style={{ fontSize:12, color:C.muted, marginTop:4, marginBottom:0 }}>
              Upload a CSV of leads to enroll them when the campaign is saved. The campaign will auto-activate immediately.
              Same format as Leads &amp; CRM — columns: <code style={{ fontSize:11 }}>name</code>, <code style={{ fontSize:11 }}>phone</code> (required) · <code style={{ fontSize:11 }}>city</code>, <code style={{ fontSize:11 }}>company</code> (optional).
            </p>
          </div>
          {csvLeads.length > 0 && (
            <button onClick={()=>setCsvLeads([])}
              style={{ background:'none', border:`1px solid ${C.danger}40`, color:C.danger, borderRadius:7, padding:'4px 12px', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:F, whiteSpace:'nowrap', marginLeft:12 }}>
              ✕ Clear
            </button>
          )}
        </div>

        {csvLeads.length === 0 ? (
          <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            border:`2px dashed ${C.border}`, borderRadius:10, padding:'28px 20px', cursor:'pointer', gap:8,
            background:'#FAFBFC', transition:'border-color 0.15s' }}
            onMouseEnter={e=>e.currentTarget.style.borderColor=C.wa}
            onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
            <div style={{ fontSize:32 }}>📁</div>
            <div style={{ fontSize:13, fontWeight:700, color:C.textSub }}>Click to upload leads CSV</div>
            <div style={{ fontSize:11, color:C.muted }}>Duplicates (same phone) are skipped automatically</div>
            <input type="file" accept=".csv" style={{ display:'none' }} onChange={handleCampaignCSV} />
          </label>
        ) : (
          <div>
            <div style={{ background:C.waLight, border:`1px solid ${C.wa}40`, borderRadius:10, padding:'12px 16px', marginBottom:14, display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ fontSize:26 }}>✅</div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:C.waDark }}>{csvLeads.length} leads ready to enroll</div>
                <div style={{ fontSize:12, color:C.wa }}>These will be imported, enrolled, and the campaign will auto-activate on save.</div>
              </div>
            </div>
            <div style={{ border:`1px solid ${C.border}`, borderRadius:9, overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr 2fr', background:'#F3F4F6', padding:'8px 14px', fontSize:11, fontWeight:700, color:C.muted, gap:10 }}>
                <span>Name</span><span>Phone</span><span>City</span><span>Company</span>
              </div>
              {csvLeads.slice(0,6).map((l,i)=>(
                <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr 2fr', padding:'8px 14px', borderTop:`1px solid ${C.border}`, fontSize:12, color:C.text, gap:10 }}>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.name}</span>
                  <span style={{ color:C.muted }}>{l.phone}</span>
                  <span style={{ color:C.muted }}>{l.city||'—'}</span>
                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:C.muted }}>{l.company||'—'}</span>
                </div>
              ))}
              {csvLeads.length > 6 && (
                <div style={{ padding:'8px 14px', borderTop:`1px solid ${C.border}`, fontSize:11, color:C.muted, textAlign:'center' }}>
                  ...and {csvLeads.length - 6} more leads
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );

  return (
    <div style={{ padding:28, overflowY:'auto', height:'100%' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:C.text }}>Campaigns</h2>
          <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>{campaigns.length} total · {campaigns.filter(c=>c.status==='active').length} active</p>
        </div>
        <Btn onClick={()=>setCreating(true)}>+ New Campaign</Btn>
      </div>
      <Alert msg={err} onClose={()=>setErr('')} />
      <Alert msg={msg} type="success" onClose={()=>setMsg('')} />

      {/* Enroll modal */}
      {enrollModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Card style={{ width:560, maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}` }}>
              <h3 style={{ fontSize:16, fontWeight:800, color:C.text }}>Enroll Leads & Activate Campaign</h3>
              <p style={{ fontSize:12, color:C.muted, marginTop:4 }}>Select leads to enroll. Campaign will auto-activate.</p>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'14px 24px' }}>
              <div style={{ marginBottom:10, fontSize:12, color:C.muted }}>
                <label><input type="checkbox" style={{ accentColor:C.wa, marginRight:8 }}
                  checked={selectedLeads.length===leads.length} onChange={e=>setSelectedLeads(e.target.checked?leads.map(l=>l.id):[])} />
                  Select all ({leads.length} leads)</label>
              </div>
              {leads.map(lead=>(
                <label key={lead.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:`1px solid ${C.border}`, cursor:'pointer' }}>
                  <input type="checkbox" checked={selectedLeads.includes(lead.id)} style={{ accentColor:C.wa }}
                    onChange={e=>setSelectedLeads(prev=>e.target.checked?[...prev,lead.id]:prev.filter(id=>id!==lead.id))} />
                  <Av text={lead.name.split(' ').map(n=>n[0]).join('').slice(0,2)} size={30} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{lead.name}</div>
                    <div style={{ fontSize:11, color:C.muted }}>{lead.phone} · {lead.city} · {lead.pipeline_stage}</div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ padding:'16px 24px', borderTop:`1px solid ${C.border}`, display:'flex', gap:10, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={()=>setEnrollModal(null)}>Cancel</Btn>
              <Btn onClick={doEnroll} disabled={!selectedLeads.length}>Enroll {selectedLeads.length} Leads & Activate</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {['all','active','paused','scheduled','completed'].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} style={{
            padding:'6px 16px', borderRadius:20, border:`1px solid ${filter===f?C.wa:C.border}`,
            background:filter===f?C.waLight:'#fff', color:filter===f?C.waDark:C.textSub,
            fontSize:12, fontWeight:filter===f?700:500, cursor:'pointer', fontFamily:F, textTransform:'capitalize' }}>{f}</button>
        ))}
      </div>

      {filtered.length===0 && (
        <Card style={{ padding:40, textAlign:'center' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>🚀</div>
          <div style={{ fontSize:15, fontWeight:700, color:C.textSub }}>No campaigns yet</div>
          <div style={{ fontSize:13, color:C.muted, marginBottom:16 }}>Create your first campaign to start sending messages</div>
          <Btn onClick={()=>setCreating(true)}>+ New Campaign</Btn>
        </Card>
      )}

      <div style={{ display:'grid', gap:14 }}>
        {filtered.map(c=>(
          <Card key={c.id} style={{ padding:22 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
              <div>
                <div style={{ fontWeight:800, fontSize:15, color:C.text, marginBottom:6 }}>{c.name}</div>
                <div style={{ display:'flex', gap:6 }}>
                  {(c.tags||[]).map(t=><Badge key={t} label={t} color={C.muted} bg="#F3F4F6" />)}
                </div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <StatusBadge status={c.status} />
                {(c.status==='active'||c.status==='paused') && (
                  <Btn variant={c.status==='active'?'secondary':'accent'} size="sm" onClick={()=>toggleStatus(c.id,c.status)}>
                    {c.status==='active'?'⏸ Pause':'▶ Resume'}
                  </Btn>
                )}
                {(c.status==='draft'||c.status==='paused') && (
                  <Btn size="sm" onClick={()=>openEnroll(c.id)}>👥 Enroll Leads</Btn>
                )}
                {c.status==='active' && (
                  <Btn variant="accent" size="sm" onClick={()=>{ API.sendNow(c.id); setMsg('Send cycle triggered!'); }}>⚡ Send Now</Btn>
                )}
                <Btn variant="ghost" size="sm" onClick={()=>startEditCampaign(c.id)} title="Edit campaign">✏️</Btn>
                <Btn variant="ghost" size="sm" onClick={()=>doDuplicateCampaign(c.id)} title="Duplicate campaign">📋</Btn>
                <Btn variant="danger" size="sm" onClick={()=>{ if(window.confirm('Delete campaign?')) API.deleteCampaign(c.id).then(load); }}>🗑</Btn>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:16 }}>
              {[['Leads',c.total_leads||0,C.accent],['Sent',c.total_sent||0,C.wa],['Delivered',c.total_delivered||0,C.success],['Replies',c.total_replies||0,C.purple],['Won',c.total_conversions||0,C.warning]].map(([l,v,col])=>(
                <div key={l} style={{ textAlign:'center', background:'#F9FAFB', borderRadius:9, padding:'12px 6px', border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:20, fontWeight:800, color:col }}>{Number(v).toLocaleString()}</div>
                  <div style={{ fontSize:10, color:C.muted, fontWeight:600 }}>{l}</div>
                </div>
              ))}
            </div>
            <Bar value={c.total_sent||0} max={c.total_leads||1}
              color={c.status==='active'?C.wa:c.status==='completed'?C.success:C.warning} />
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:6, fontSize:11, color:C.muted }}>
              <span>{c.sequence_count||0} messages · {c.number_count||0} numbers</span>
              <span>{Math.round(((c.total_sent||0)/Math.max(c.total_leads||1,1))*100)}% complete</span>
            </div>
            <div style={{ display:'flex', gap:16, marginTop:8, fontSize:10, color:C.muted, borderTop:`1px solid ${C.border}`, paddingTop:8 }}>
              <span>🕐 Created: {c.created_at ? new Date(c.created_at).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</span>
              <span>⚡ Last sent: {c.last_run_at ? new Date(c.last_run_at).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : 'Never'}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── INBOX ───────────────────────────────────────────────────
function Inbox({ onNav }) {
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [input, setInput] = useState('');
  const [aiReply, setAiReply] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(localStorage.getItem('wa_inbox_account') || '');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const endRef = useRef(null);
  const connectedAccounts = accounts.filter(a=>a.status==='connected');

  // Load WA accounts and pick a sensible default selection
  useEffect(()=>{
    API.getAccounts().then(r=>{
      const list = r.data || [];
      setAccounts(list);
      const stillValid = list.some(a => String(a.id)===String(accountId) && a.status==='connected');
      if (!stillValid) {
        const firstConnected = list.find(a => a.status==='connected');
        const id = firstConnected ? String(firstConnected.id) : '';
        setAccountId(id);
        localStorage.setItem('wa_inbox_account', id);
      }
    });
  }, []);

  const selectAccount = (id) => {
    setAccountId(id);
    localStorage.setItem('wa_inbox_account', id);
    setActiveChat(null);
    setMessages([]);
  };

  const loadChats = useCallback(() => API.getInbox({ search, account_id: accountId || undefined }).then(r=>setChats(r.data)), [search, accountId]);
  useEffect(()=>{ loadChats(); }, [loadChats]);

  // Listen for inbox_update (new inbound WA message) → refresh chat list
  useEffect(()=>{
    const sock = getSocket();
    console.log ("inbox_update called");
    sock.on('inbox_update', () => loadChats());
    return ()=>{ sock.off('inbox_update'); };
  }, [loadChats]);

  // Keep the account dropdown's connected/disconnected state live
  useEffect(()=>{
    const sock = getSocket();
    const refreshAccounts = () => API.getAccounts().then(r=>setAccounts(r.data || []));
    sock.on('account_status', refreshAccounts);
    return ()=>{ sock.off('account_status', refreshAccounts); };
  }, []);

  // Listen for sync_complete from the backend messaging-history handler
  useEffect(()=>{
    const sock = getSocket();
    const onSyncComplete = ({ accountId: syncedId, stored, total }) => {
      if (String(syncedId) === String(accountId)) {
        setSyncLoading(false);
        setSyncResult({ stored, total });
        loadChats();
        setTimeout(()=>setSyncResult(null), 6000);
      }
    };
    sock.on('sync_complete', onSyncComplete);
    return ()=>{ sock.off('sync_complete', onSyncComplete); };
  }, [accountId, loadChats]);

  useEffect(()=>{
    if (!activeChat) return;
    API.getMessages(activeChat.lead_id, accountId).then(r=>setMessages(r.data));
    const sock = getSocket();
    sock.emit('join_lead', activeChat.lead_id);
    sock.on('new_message', msg => {
      if (msg.lead_id===activeChat.lead_id) {
        setMessages(prev=>[...prev,msg]);
        loadChats();
      }
    });
    return ()=>{ sock.emit('leave_lead', activeChat.lead_id); sock.off('new_message'); };
  }, [activeChat, accountId, loadChats]);

  useEffect(()=>{ endRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  // Auto-open conversation when navigated from LeadsCRM chat initiation
  useEffect(()=>{
    const targetId = localStorage.getItem('wa_inbox_open_lead');
    if (!targetId || !chats.length) return;
    const chat = chats.find(c => String(c.lead_id) === targetId);
    if (chat) {
      setActiveChat(chat);
      localStorage.removeItem('wa_inbox_open_lead');
    }
  }, [chats]);

  const send = async () => {
    if (!input.trim() || !activeChat) return;
    if (!accountId) { alert('Select a connected WhatsApp account first'); return; }
    const text = input; setInput('');
    try {
      await API.sendMessage({ lead_id:activeChat.lead_id, content:text, account_id:accountId });
      loadChats();
    } catch(e) { setInput(text); alert(e.response?.data?.error||'Send failed'); }
  };

  const genAI = async () => {
    setAiLoading(true); setAiReply(null);
    const last = [...messages].reverse().find(m=>m.direction==='inbound');
    try {
      const r = await API.generateAIReply(activeChat.name, activeChat.campaign_name||'', activeChat.pipeline_stage||'', last?.content||'');
      setAiReply(r);
    } catch { setAiReply('Thanks for reaching out! I would love to tell you more. When would be a good time for a quick call?'); }
    setAiLoading(false);
  };

  const doSync = async () => {
    if (!accountId) { alert('Select a connected WhatsApp account first'); return; }
    const confirmed = window.confirm(
      'Fetching old chats requires disconnecting WhatsApp and scanning a new QR code.\n\n' +
      'WhatsApp only delivers full history (up to 90 days) on a fresh device link — not on reconnect.\n\n' +
      'Click OK to proceed. You will be taken to the WA Accounts page to scan the new QR code.'
    );
    if (!confirmed) return;
    setSyncLoading(true); setSyncResult(null);
    try {
      const res = await API.syncChats(accountId);
      if (res.data?.needs_qr) {
        setSyncLoading(false);
        setSyncResult({ needs_qr: true });
        // Navigate to Accounts page after 2s so user can scan QR
        setTimeout(() => { if (onNav) onNav('accounts'); }, 2000);
      } else {
        // Fallback: wait for sync_complete socket event
        setTimeout(() => setSyncLoading(false), 30000);
      }
    } catch(e) {
      setSyncLoading(false);
      alert(e.response?.data?.error || 'Sync failed');
    }
  };

  const stageColor = s => ({'Interested':C.wa,'Negotiation':C.warning,'Contacted':C.accent,'New Lead':C.muted,'Won':C.success,'Lost':C.danger})[s]||C.muted;

  // Format timestamp: time only for today, "Yesterday HH:MM" or "MMM D, HH:MM" for older
  const fmtChatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const time = d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === now.toDateString()) return time;
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric' }) + ', ' + time;
  };
  const fmtMsgTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const time = d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === now.toDateString()) return `Today, ${time}`;
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return `Yesterday, ${time}`;
    return d.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' }) + ', ' + time;
  };

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>
      {/* Chat list */}
      <div style={{ width:300, background:'#fff', borderRight:`1px solid ${C.border}`, display:'flex', flexDirection:'column', flexShrink:0 }}>
        <div style={{ padding:'14px 14px 10px', borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div style={{ fontWeight:800, fontSize:14, color:C.text }}>Inbox
              <span style={{ fontSize:11, background:C.dangerBg, color:C.danger, padding:'2px 7px', borderRadius:10, fontWeight:700, marginLeft:8 }}>
                {chats.filter(c=>c.unread>0).length} unread
              </span>
            </div>
            {connectedAccounts.length > 0 && (
              <button onClick={doSync} disabled={syncLoading} title="Fetch older chats from WhatsApp" style={{
                fontSize:11, fontWeight:600, padding:'4px 10px', borderRadius:7, cursor:syncLoading?'not-allowed':'pointer',
                background:syncLoading?C.bg:C.waLight, color:C.waDark, border:`1px solid ${C.wa}50`,
                fontFamily:'inherit', opacity:syncLoading?0.7:1 }}>
                {syncLoading ? '⟳ Syncing…' : '↻ Sync Old Chats'}
              </button>
            )}
          </div>
          {syncResult && (
            <div style={{ fontSize:11, marginBottom:8, padding:'5px 9px', borderRadius:7,
              background: syncResult.needs_qr ? C.warningBg : (syncResult.stored>0 ? C.successBg : C.bg),
              color: syncResult.needs_qr ? C.warning : (syncResult.stored>0 ? C.success : C.muted),
              border:`1px solid ${syncResult.needs_qr ? C.warning : (syncResult.stored>0 ? C.success : C.border)}40` }}>
              {syncResult.needs_qr
                ? '📷 Redirecting to Accounts — scan the new QR code to fetch full history…'
                : syncResult.stored > 0
                  ? `✓ ${syncResult.stored} new message${syncResult.stored>1?'s':''} imported`
                  : 'Inbox already up to date'}
            </div>
          )}
          <Sel value={accountId} onChange={e=>selectAccount(e.target.value)} style={{ fontSize:12, marginBottom:10 }}>
            {connectedAccounts.length===0 && <option value="">No connected accounts</option>}
            {connectedAccounts.map(a=>(
              <option key={a.id} value={a.id}>● {a.name||a.phone}</option>
            ))}
          </Sel>
          <Input placeholder="Search conversations..." value={search} onChange={e=>setSearch(e.target.value)} style={{ fontSize:12 }} />
        </div>
        <div style={{ flex:1, overflowY:'auto' }}>
          {chats.length===0 && <div style={{ padding:20, textAlign:'center', color:C.muted, fontSize:13 }}>No conversations yet</div>}
          {chats.map(chat=>(
            <div key={chat.lead_id} onClick={()=>setActiveChat(chat)} style={{
              padding:'12px 14px', cursor:'pointer', borderBottom:`1px solid ${C.border}`,
              background:activeChat?.lead_id===chat.lead_id?C.waLight:'#fff',
              borderLeft:activeChat?.lead_id===chat.lead_id?`3px solid ${C.wa}`:'3px solid transparent',
              transition:'all 0.1s' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <Av text={chat.name.split(' ').map(n=>n[0]).join('').slice(0,2)} size={30} bg={C.waLight} color={C.waDark} />
                  <span style={{ fontWeight:700, fontSize:13, color:C.text }}>{chat.name}</span>
                </div>
                <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                  <span style={{ fontSize:10, color:C.muted }}>{fmtChatTime(chat.last_at)}</span>
                  {chat.unread>0 && <span style={{ fontSize:10, background:C.wa, color:'#fff', borderRadius:10, padding:'1px 6px', fontWeight:700 }}>{chat.unread}</span>}
                </div>
              </div>
              <div style={{ fontSize:11, color:C.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', paddingLeft:38, marginBottom:4 }}>
                {chat.last_direction==='outbound'?'You: ':''}{chat.last_message}
              </div>
              <div style={{ paddingLeft:38 }}>
                <Badge label={chat.pipeline_stage||'New Lead'} color={stageColor(chat.pipeline_stage)} bg={`${stageColor(chat.pipeline_stage)}15`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      {!activeChat ? (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', background:'#F8F9FB' }}>
          <div style={{ textAlign:'center', color:C.muted }}>
            <div style={{ fontSize:48, marginBottom:12 }}>💬</div>
            <div style={{ fontSize:15, fontWeight:700, color:C.textSub }}>Select a conversation</div>
            <div style={{ fontSize:13 }}>Choose a lead from the left to view messages</div>
          </div>
        </div>
      ) : (
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#F8F9FB' }}>
          {/* Header */}
          <div style={{ padding:'12px 20px', borderBottom:`1px solid ${C.border}`, background:'#fff', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            <div style={{ display:'flex', gap:12, alignItems:'center' }}>
              <Av text={activeChat.name.split(' ').map(n=>n[0]).join('').slice(0,2)} size={38} bg={C.waLight} color={C.waDark} />
              <div>
                <div style={{ fontWeight:800, fontSize:14, color:C.text }}>{activeChat.name}</div>
                <div style={{ fontSize:11, color:C.muted }}>{activeChat.phone} · {activeChat.campaign_name}</div>
              </div>
              <Badge label={activeChat.pipeline_stage||'New Lead'} color={stageColor(activeChat.pipeline_stage)} bg={`${stageColor(activeChat.pipeline_stage)}15`} />
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex:1, overflowY:'auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:10 }}>
            {messages.map((msg,i)=>(
              <div key={i} style={{ display:'flex', justifyContent:msg.direction==='outbound'?'flex-end':'flex-start' }}>
                <div style={{
                  maxWidth:'62%', padding:'10px 14px', lineHeight:1.55,
                  borderRadius:msg.direction==='outbound'?'16px 4px 16px 16px':'4px 16px 16px 16px',
                  background:msg.direction==='outbound'?C.wa:'#fff',
                  boxShadow:C.shadow, border:msg.direction==='outbound'?'none':`1px solid ${C.border}`,
                  fontSize:13, color:msg.direction==='outbound'?'#fff':C.text }}>
                  {msg.content}
                  <div style={{ fontSize:10, opacity:0.7, marginTop:4, textAlign:'right' }}>
                    {fmtMsgTime(msg.sent_at || msg.created_at)}
                    {msg.direction==='outbound' && ` · ${msg.status||'sent'}`}
                  </div>
                </div>
              </div>
            ))}
            {aiReply && (
              <div style={{ background:'#fff', border:`1.5px solid ${C.purple}30`, borderRadius:12, padding:14, margin:'4px 0', boxShadow:C.shadow }}>
                <div style={{ fontSize:11, color:C.purple, fontWeight:700, marginBottom:8 }}>🤖 AI Suggested Reply</div>
                <div style={{ fontSize:13, color:C.text, lineHeight:1.6, marginBottom:10 }}>{aiReply}</div>
                <div style={{ display:'flex', gap:8 }}>
                  <Btn size="sm" onClick={()=>{ setInput(aiReply); setAiReply(null); }} style={{ background:C.purple, color:'#fff', border:'none' }}>Use Reply</Btn>
                  <Btn variant="ghost" size="sm" onClick={()=>setAiReply(null)}>Dismiss</Btn>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div style={{ padding:'12px 20px', background:'#fff', borderTop:`1px solid ${C.border}`, flexShrink:0 }}>
            <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
              <textarea value={input} onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&(e.preventDefault(),send())}
                rows={2} placeholder="Type a message... (Enter to send)"
                style={{ flex:1, background:'#F9FAFB', border:`1.5px solid ${C.border}`, borderRadius:10, padding:'10px 14px', color:C.text, fontSize:13, fontFamily:F, resize:'none' }} />
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <Btn onClick={genAI} disabled={aiLoading} style={{ background:C.purpleBg, color:C.purple, border:`1px solid ${C.purple}30`, fontSize:11, padding:'7px 12px' }}>
                  {aiLoading?'...':'🤖 AI'}
                </Btn>
                <Btn onClick={send}>Send</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LEADS & CRM ─────────────────────────────────────────────
function LeadsCRM({ onNav }) {
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [stats, setStats] = useState({});
  const [view, setView] = useState('table');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ name:'', phone:'', email:'', country:'', assigned_to:'', pipeline_stage:'New', source:'', tags:'' });
  const [sources, setSources] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [countries, setCountries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [idFilter, setIdFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // WA chat initiation
  const [chatModal, setChatModal] = useState(null); // { name, phone } or null
  const [chatAccounts, setChatAccounts] = useState([]);
  const [chatAccountId, setChatAccountId] = useState('');
  const [chatContent, setChatContent] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const STAGES = ['New','Hot','Warm','Cold','Budget','General','Closed-Won','Lost'];
  const stageColor = s => ({'New':'#28B8DA','Hot':'#E53E3E','Warm':'#DD6B20','Cold':'#718096','Budget':'#805AD5','General':'#A0AEC0','Closed-Won':'#38A169','Lost':'#C53030'})[s]||C.muted;

  const load = async () => {
    const params = {};
    if (search)       params.search    = search;
    if (stageFilter!=='all') params.stage = stageFilter;
    if (idFilter)     params.id        = idFilter;
    if (tagFilter)    params.tag       = tagFilter;
    if (dateFrom)     params.date_from = dateFrom;
    if (dateTo)       params.date_to   = dateTo;
    const [l, p, s, src, tgs, ctr, stf] = await Promise.all([
      API.getLeads({ ...params, limit:200 }),
      API.getPipeline(),
      API.getLeadStats(),
      API.getLeadSources(),
      API.getLeadTags(),
      API.getLeadCountries(),
      API.getLeadStaff(),
    ]);
    setLeads(l.data.leads); setPipeline(p.data); setStats(s.data);
    setSources(src.data || []);
    setAvailableTags(tgs.data || []);
    setCountries(ctr.data || []);
    setStaff(stf.data || []);
    setLoading(false);
  };

  const clearFilters = () => { setSearch(''); setStageFilter('all'); setIdFilter(''); setTagFilter(''); setDateFrom(''); setDateTo(''); };
  const hasFilters = search || stageFilter!=='all' || idFilter || tagFilter || dateFrom || dateTo;

  useEffect(()=>{ load(); }, [search, stageFilter, idFilter, tagFilter, dateFrom, dateTo]);

  const addLead = async () => {
    if (!form.name||!form.phone) return setErr('Name and phone required');
    try {
      const tags = form.tags.split(',').map(t=>t.trim()).filter(Boolean);
      await API.createLead({ ...form, tags });
      setAddOpen(false);
      setForm({ name:'', phone:'', email:'', country:'', assigned_to:'', pipeline_stage:'New', source:'', tags:'' });
      load(); setMsg('Lead added!');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const changeStage = async (id, stage) => {
    await API.updateLead(id, { pipeline_stage:stage });
    load();
  };

  const openChatModal = async (lead) => {
    const r = await API.getAccounts();
    const connected = (r.data || []).filter(a => a.status === 'connected');
    setChatAccounts(connected);
    // Pre-select primary, then first connected
    const primary = connected.find(a => a.is_primary) || connected[0];
    setChatAccountId(primary ? String(primary.id) : '');
    setChatContent('');
    setChatModal({ name: lead.name, phone: lead.phone });
  };

  const startChat = async () => {
    if (!chatModal) return;
    if (!chatAccountId) { alert('Please select a connected WhatsApp account'); return; }
    setChatLoading(true);
    try {
      const r = await API.initiateChat({
        name: chatModal.name,
        phone: chatModal.phone,
        account_id: parseInt(chatAccountId),
        content: chatContent.trim() || undefined,
      });
      localStorage.setItem('wa_inbox_open_lead', String(r.data.lead_id));
      localStorage.setItem('wa_inbox_account', chatAccountId);
      setChatModal(null);
      if (onNav) onNav('inbox');
    } catch(e) {
      alert(e.response?.data?.error || 'Failed to initiate chat');
    }
    setChatLoading(false);
  };

  const downloadSampleCSV = () => {
    const rows = [
      'name,phone,city,company',
      'Rajesh Kumar,919876543210,Delhi,AutoFleet Pvt Ltd',
      'Priya Mehta,918765432109,Mumbai,QuickRide Solutions',
      'Amit Sharma,917654321098,Bangalore,SpeedLogistics',
      'Sunita Verma,916543210987,Chennai,',
      'Rahul Gupta,915432109876,,Fleet Masters',
    ].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'leads_import_sample.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  const handleCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header:true, skipEmptyLines:true,
      complete: async (results) => {
        const mapped = results.data.map(r=>({ name:r.name||r.Name, phone:r.phone||r.Phone||r.mobile||r.Mobile, city:r.city||r.City, company:r.company||r.Company })).filter(r=>r.name&&r.phone);
        if (!mapped.length) return setErr('No valid rows found. Columns needed: name, phone');
        const r = await API.bulkImport(mapped);
        setImportOpen(false); load();
        setMsg(`Imported ${r.data.imported} leads · Skipped ${r.data.skipped} duplicates`);
      }
    });
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding:28, overflowY:'auto', height:'100%' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:C.text }}>Leads & CRM</h2>
          <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>{stats.total||0} total · {stats.won||0} closed-won · {stats.interested||0} hot</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Btn variant="ghost" onClick={downloadSampleCSV}>📄 Sample CSV</Btn>
          <Btn variant="ghost" onClick={()=>setImportOpen(true)}>📥 Import CSV</Btn>
          <Btn onClick={()=>setAddOpen(true)}>+ Add Lead</Btn>
        </div>
      </div>

      <Alert msg={err} onClose={()=>setErr('')} />
      <Alert msg={msg} type="success" onClose={()=>setMsg('')} />

      {/* CSV import modal */}
      {importOpen && (
        <Card style={{ padding:22, marginBottom:20, border:`2px solid ${C.accent}30` }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:10 }}>Import Leads from CSV</h3>
          <p style={{ fontSize:13, color:C.muted, marginBottom:14 }}>CSV must have columns: <code>name</code>, <code>phone</code> (required), <code>city</code>, <code>company</code> (optional)</p>
          <div style={{ display:'flex', gap:10 }}>
            <input type="file" accept=".csv" onChange={handleCSV} style={{ flex:1 }} />
            <Btn variant="ghost" onClick={()=>setImportOpen(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      {/* Add lead modal */}
      {addOpen && (
        <Card style={{ padding:22, marginBottom:20, border:`2px solid ${C.wa}30` }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>Add New Lead</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
            {[['Name *','name','Rajesh Kumar'],['Phone *','phone','+91 98765 43210'],['Email','email','email@domain.com'],
              ['Tags','tags','hot, fleet']].map(([l,k,ph])=>(
              <div key={k}>
                <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>{l}</label>
                <Input placeholder={ph} value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} />
              </div>
            ))}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Pipeline Stage</label>
              <Sel value={form.pipeline_stage} onChange={e=>setForm(f=>({...f,pipeline_stage:e.target.value}))}>
                {STAGES.map(s=><option key={s}>{s}</option>)}
              </Sel>
            </div>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Source</label>
              <Sel value={form.source} onChange={e=>setForm(f=>({...f,source:e.target.value}))}>
                <option value="">— Select source —</option>
                {sources.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
              </Sel>
            </div>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Country</label>
              <Sel value={form.country} onChange={e=>setForm(f=>({...f,country:e.target.value}))}>
                <option value="">— Select country —</option>
                {countries.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </Sel>
            </div>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Assigned To</label>
              <Sel value={form.assigned_to} onChange={e=>setForm(f=>({...f,assigned_to:e.target.value}))}>
                <option value="">— Unassigned —</option>
                {staff.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </Sel>
            </div>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={addLead}>Save Lead</Btn>
            <Btn variant="ghost" onClick={()=>setAddOpen(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      {/* Stage stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:10, marginBottom:20 }}>
        {STAGES.map(stage=>(
          <div key={stage} onClick={()=>setStageFilter(stageFilter===stage?'all':stage)}
            style={{ textAlign:'center', padding:'12px 6px', background:'#fff', border:`1px solid ${stageFilter===stage?stageColor(stage):C.border}`,
              borderRadius:10, boxShadow:C.shadow, borderTop:`3px solid ${stageColor(stage)}`, cursor:'pointer' }}>
            <div style={{ fontSize:22, fontWeight:800, color:stageColor(stage) }}>
              {pipeline[stage]?.length||0}
            </div>
            <div style={{ fontSize:10, color:C.muted, fontWeight:600 }}>{stage}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
        <div style={{ display:'flex', gap:10 }}>
          <Input placeholder="🔍 Search name / phone / company..." value={search} onChange={e=>setSearch(e.target.value)} style={{ flex:2 }} />
          <Input placeholder="Lead ID" value={idFilter} onChange={e=>setIdFilter(e.target.value)} style={{ width:100 }} type="number" />
          <Sel value={tagFilter} onChange={e=>setTagFilter(e.target.value)} style={{ flex:1 }}>
            <option value="">All Tags</option>
            {availableTags.map(t=><option key={t.name} value={t.name}>{t.name} ({t.count})</option>)}
          </Sel>
          <Sel value={stageFilter} onChange={e=>setStageFilter(e.target.value)} style={{ flex:1 }}>
            <option value="all">All Stages</option>
            {STAGES.map(s=><option key={s} value={s}>{s}</option>)}
          </Sel>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <label style={{ fontSize:11, color:C.muted, fontWeight:600, whiteSpace:'nowrap' }}>Date Range:</label>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
            style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 10px', fontSize:12, fontFamily:F, color:C.text }} />
          <span style={{ color:C.muted, fontSize:12 }}>to</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
            style={{ border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 10px', fontSize:12, fontFamily:F, color:C.text }} />
          {hasFilters && <Btn variant="ghost" onClick={clearFilters} style={{ fontSize:11, padding:'5px 12px' }}>✕ Clear filters</Btn>}
          <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
            {['table','pipeline'].map(v=>(
              <Btn key={v} onClick={()=>setView(v)} variant={view===v?'primary':'ghost'} style={view===v?{background:C.wa,border:'none',color:'#fff'}:{}}>
                {v==='table'?'☰ Table':'⊞ Pipeline'}
              </Btn>
            ))}
          </div>
        </div>
      </div>

      {view==='table' ? (
        <Card style={{ overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:`1px solid ${C.border}`, background:'#FAFBFC' }}>
                {['ID','Name','Phone','Country','Tags','Status','Assigned To','Added On','Actions'].map(h=>(
                  <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:11, color:C.muted, fontWeight:700, letterSpacing:0.5, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map((lead,i)=>(
                <tr key={lead.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===1?'#FAFBFC':'#fff' }}>
                  <td style={{ padding:'11px 16px', fontSize:11, color:C.muted, fontWeight:600 }}>#{lead.id}</td>
                  <td style={{ padding:'11px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Av text={lead.name.split(' ').map(n=>n[0]).join('').slice(0,2)} size={30} />
                      <span style={{ fontSize:13, fontWeight:600, color:C.text }}>{lead.name}</span>
                    </div>
                  </td>
                  <td style={{ padding:'11px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <span style={{ fontSize:12, color:C.muted }}>{lead.phone}</span>
                      <button onClick={()=>openChatModal(lead)} title="Start WhatsApp Chat"
                        style={{ background:C.waLight, border:`1px solid ${C.wa}40`, color:C.waDark, cursor:'pointer', fontSize:12, borderRadius:6, padding:'2px 7px', fontFamily:'inherit', fontWeight:700, lineHeight:1.4, flexShrink:0 }}>
                        💬
                      </button>
                    </div>
                  </td>
                  <td style={{ padding:'11px 16px', fontSize:12, color:C.textSub }}>{lead.country||'—'}</td>
                  <td style={{ padding:'11px 16px', maxWidth:160 }}>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                      {(lead.tags||[]).slice(0,3).map(t=>(
                        <span key={t} onClick={()=>setTagFilter(t)} title="Filter by this tag"
                          style={{ fontSize:10, background:'#EEF2FF', color:'#4F46E5', borderRadius:6, padding:'2px 6px',
                            fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>{t}</span>
                      ))}
                      {(lead.tags||[]).length>3 && <span style={{ fontSize:10, color:C.muted }}>+{lead.tags.length-3}</span>}
                    </div>
                  </td>
                  <td style={{ padding:'11px 16px' }}>
                    <select value={lead.pipeline_stage} onChange={e=>changeStage(lead.id,e.target.value)}
                      style={{ fontSize:11, color:stageColor(lead.pipeline_stage), background:`${stageColor(lead.pipeline_stage)}15`,
                        border:`1px solid ${stageColor(lead.pipeline_stage)}40`, borderRadius:10, padding:'3px 8px',
                        cursor:'pointer', fontFamily:F, fontWeight:600 }}>
                      {STAGES.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{ padding:'11px 16px', fontSize:12, color:C.textSub, whiteSpace:'nowrap' }}>{lead.assigned_name?.trim()||'—'}</td>
                  <td style={{ padding:'11px 16px', fontSize:11, color:C.muted, whiteSpace:'nowrap' }}>
                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                  </td>
                  <td style={{ padding:'11px 16px' }}>
                    <button onClick={()=>{ if(window.confirm('Delete lead?')) API.deleteLead(lead.id).then(load); }}
                      style={{ background:'none', border:'none', color:C.danger, cursor:'pointer', fontSize:14 }}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {leads.length===0 && <div style={{ padding:40, textAlign:'center', color:C.muted, fontSize:13 }}>No leads found</div>}
        </Card>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:`repeat(${STAGES.length},1fr)`, gap:10 }}>
          {STAGES.map(stage=>(
            <div key={stage} style={{ background:'#F9FAFB', border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
              <div style={{ padding:'10px 12px', background:'#fff', borderBottom:`2px solid ${stageColor(stage)}`,
                display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:11, fontWeight:800, color:stageColor(stage), textTransform:'uppercase', letterSpacing:0.5 }}>{stage}</span>
                <span style={{ fontSize:11, background:`${stageColor(stage)}15`, color:stageColor(stage), borderRadius:10, padding:'1px 7px', fontWeight:700 }}>
                  {pipeline[stage]?.length||0}
                </span>
              </div>
              <div style={{ padding:8 }}>
                {(pipeline[stage]||[]).map(lead=>(
                  <div key={lead.id} style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius:9, padding:'10px 12px', marginBottom:8, boxShadow:C.shadow }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{lead.name}</div>
                    <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>{lead.company||lead.phone}</div>
                    {(lead.tags||[]).slice(0,2).map(t=><Badge key={t} label={t} color={C.muted} bg="#F3F4F6" />)}
                  </div>
                ))}
                {!(pipeline[stage]||[]).length && <div style={{ textAlign:'center', padding:'16px 0', color:C.muted, fontSize:11 }}>Empty</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── WA Chat Initiation Modal ─────────────────────────── */}
      {chatModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:28, width:440, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:C.text }}>Start WhatsApp Chat</div>
                <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>{chatModal.name} · {chatModal.phone}</div>
              </div>
              <button onClick={()=>setChatModal(null)} style={{ background:'none', border:'none', fontSize:20, color:C.muted, cursor:'pointer', lineHeight:1 }}>✕</button>
            </div>

            {chatAccounts.length === 0 ? (
              <div style={{ padding:'20px 0', textAlign:'center', color:C.muted, fontSize:13 }}>
                No connected WhatsApp accounts.<br/>
                <span style={{ fontSize:12 }}>Connect a number from <b>WA Accounts</b> first.</span>
              </div>
            ) : (
              <>
                <div style={{ marginBottom:14 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.muted, display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Send From</label>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {chatAccounts.map(a=>(
                      <label key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:10, border:`1.5px solid ${chatAccountId===String(a.id)?C.wa:C.border}`, background:chatAccountId===String(a.id)?C.waLight:'#FAFBFC', cursor:'pointer' }}>
                        <input type="radio" name="chatAccount" value={a.id} checked={chatAccountId===String(a.id)} onChange={()=>setChatAccountId(String(a.id))} style={{ accentColor:C.wa }} />
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{a.name||a.phone}</div>
                          <div style={{ fontSize:11, color:C.muted }}>{a.phone}</div>
                        </div>
                        {a.is_primary && <span style={{ fontSize:10, fontWeight:700, color:C.wa, background:C.waLight, border:`1px solid ${C.wa}40`, borderRadius:6, padding:'2px 7px' }}>★ Primary</span>}
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom:20 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.muted, display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>Opening Message <span style={{ fontWeight:400, textTransform:'none' }}>(optional)</span></label>
                  <textarea value={chatContent} onChange={e=>setChatContent(e.target.value)} rows={3}
                    placeholder={`Hi ${chatModal.name.split(' ')[0]}, thanks for your interest! How can I help you today?`}
                    style={{ width:'100%', boxSizing:'border-box', background:'#F9FAFB', border:`1.5px solid ${C.border}`, borderRadius:10, padding:'10px 14px', color:C.text, fontSize:13, fontFamily:F, resize:'vertical', outline:'none' }} />
                </div>

                <div style={{ display:'flex', gap:10 }}>
                  <Btn onClick={startChat} disabled={chatLoading} style={{ flex:1, justifyContent:'center', background:C.wa, color:'#fff', border:'none' }}>
                    {chatLoading ? '…' : chatContent.trim() ? '💬 Send & Open Chat' : '💬 Open Chat'}
                  </Btn>
                  <Btn variant="ghost" onClick={()=>setChatModal(null)}>Cancel</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ANALYTICS ───────────────────────────────────────────────
function Analytics() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [weekly, setWeekly] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dash, setDash] = useState({});
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [viewUserId, setViewUserId] = useState('');

  useEffect(()=>{ if (isSuperAdmin) API.getUsers().then(r=>setUsers(r.data)); }, [isSuperAdmin]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([API.getWeekly(viewUserId), API.getCampaignAnalytics(viewUserId), API.getAccountAnalytics(viewUserId), API.getDashboard(viewUserId)])
      .then(([w,c,a,d])=>{ setWeekly(w.data); setCampaigns(c.data); setAccounts(a.data); setDash(d.data); })
      .finally(()=>setLoading(false));
  }, [viewUserId]);
  useEffect(()=>{ load(); }, [load]);

  if (loading) return <Spinner />;
  const maxSent = Math.max(...weekly.map(d=>d.sent||0), 1);
  const viewingUser = users.find(u=>String(u.id)===String(viewUserId));

  return (
    <div style={{ padding:28, overflowY:'auto', height:'100%' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:C.text, marginBottom:6 }}>Analytics</h2>
          <p style={{ color:C.muted, fontSize:13 }}>
            {viewingUser ? `Viewing: ${viewingUser.name}'s activity` : 'Campaign performance and account stats · Live data from MySQL'}
          </p>
        </div>
        {isSuperAdmin && (
          <div style={{ width:220 }}>
            <Sel value={viewUserId} onChange={e=>setViewUserId(e.target.value)} style={{ fontSize:12 }}>
              <option value="">All Users (aggregate)</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.name} ({u.role.replace('_',' ')})</option>)}
            </Sel>
          </div>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:24 }}>
        {[
          {label:'Total Leads',   value:dash.total_leads||0,    color:C.accent, icon:'👥'},
          {label:'Reply Rate',    value:(dash.reply_rate||0)+'%', color:C.wa,   icon:'💬'},
          {label:'Conversions',   value:dash.conversions||0,    color:C.success,icon:'🎯'},
          {label:'Connected',     value:dash.connected_numbers||0, color:C.purple,icon:'📱'},
        ].map(m=>(
          <Card key={m.label} style={{ padding:'20px 22px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontSize:12, color:C.muted }}>{m.label}</span>
              <span style={{ fontSize:18 }}>{m.icon}</span>
            </div>
            <div style={{ fontSize:28, fontWeight:800, color:m.color, letterSpacing:-1 }}>{m.value}</div>
          </Card>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14, marginBottom:14 }}>
        <Card style={{ padding:24 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:20 }}>Daily Message Volume</h3>
          {weekly.length===0 ? (
            <div style={{ textAlign:'center', padding:30, color:C.muted, fontSize:13 }}>No campaign data yet — run a campaign to see stats here</div>
          ) : (
            <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:180 }}>
              {weekly.map((d,i)=>(
                <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center' }}>
                  <div style={{ width:'100%', display:'flex', flexDirection:'column-reverse', gap:3, height:150 }}>
                    <div style={{ width:'100%', background:C.waLight, borderRadius:'4px 4px 0 0', height:`${((d.replies||0)/maxSent)*100}%`, minHeight:2 }} />
                    <div style={{ width:'100%', background:C.wa, borderRadius:'4px 4px 0 0', height:`${((d.sent||0)/maxSent)*100}%`, minHeight:4 }} />
                  </div>
                  <div style={{ fontSize:9, color:C.muted, marginTop:5 }}>
                    {new Date(d.stat_date).toLocaleDateString('en',{weekday:'short'})}
                  </div>
                  <div style={{ fontSize:9, color:C.muted }}>{d.sent||0}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card style={{ padding:24 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:18 }}>Account Health</h3>
          {accounts.map(a=>(
            <div key={a.id} style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:5 }}>
                <span style={{ fontWeight:600, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:140 }}>{a.phone}</span>
                <StatusBadge status={a.status} />
              </div>
              <Bar value={a.daily_sent} max={a.daily_limit} h={6}
                color={a.daily_sent/Math.max(a.daily_limit,1)>0.85?C.warning:C.wa} />
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:C.muted, marginTop:3 }}>
                <span>{a.daily_sent} sent</span><span>{a.warmup_status}</span>
              </div>
            </div>
          ))}
        </Card>
      </div>

      <Card style={{ padding:24 }}>
        <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:18 }}>Campaign Performance</h3>
        {campaigns.length===0 && <div style={{ color:C.muted, fontSize:13, textAlign:'center', padding:20 }}>No campaign data yet</div>}
        {campaigns.map(c=>(
          <div key={c.id} style={{ marginBottom:16, paddingBottom:16, borderBottom:`1px solid ${C.border}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{c.name}</span>
              <div style={{ display:'flex', gap:16, fontSize:12, color:C.muted }}>
                <span>📤 {Number(c.sent||0).toLocaleString()} sent</span>
                <span>💬 {c.replies||0} replies</span>
                <span>🎯 {c.conversions||0} won</span>
                <span style={{ color:C.wa, fontWeight:700 }}>
                  {c.sent>0?((c.replies/c.sent)*100).toFixed(1):0}% reply rate
                </span>
              </div>
            </div>
            <Bar value={c.sent||0} max={c.leads||1} h={6}
              color={c.status==='active'?C.wa:c.status==='completed'?C.success:C.warning} />
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─── TEMPLATES ───────────────────────────────────────────────
function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name:'', category:'Generic', content:'', variables:'', visibility:'private' });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [aiGen, setAiGen] = useState({ open:false, sourceContent:'', provider:'anthropic', count:3, loading:false, variations:[] });
  const CATS = ['Generic','Taxi','Logistics','Food','Roadside','Rides'];

  const load = () => API.getTemplates().then(r=>setTemplates(r.data)).finally(()=>setLoading(false));
  useEffect(()=>{ load(); }, []);

  const save = async () => {
    if (!form.name||!form.content) return setErr('Name and content required');
    try {
      const vars = form.variables.split(',').map(v=>v.trim()).filter(Boolean);
      await API.createTemplate({ ...form, variables:vars });
      setAddOpen(false); setForm({ name:'', category:'Generic', content:'', variables:'', visibility:'private' });
      load(); setMsg('Template saved!');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const toggleVisibility = async t => {
    try {
      await API.updateTemplate(t.id, { visibility: t.visibility==='global'?'private':'global' });
      load();
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const openAiGen = (content) => setAiGen({ open:true, sourceContent:content, provider:'anthropic', count:3, loading:false, variations:[] });

  const runGenerate = async () => {
    if (!aiGen.sourceContent) return setErr('No message content to generate from');
    setAiGen(g=>({...g,loading:true,variations:[]}));
    try {
      const r = await API.generateVariations({ content:aiGen.sourceContent, count:aiGen.count, provider:aiGen.provider });
      setAiGen(g=>({...g,loading:false,variations:r.data.variations}));
    } catch(e) {
      setAiGen(g=>({...g,loading:false}));
      setErr(e.response?.data?.error||'Generation failed. Check your AI API key in Settings > AI Configuration.');
    }
  };

  const saveVariation = async (content, idx) => {
    const name = window.prompt(`Template name for Variation ${idx}:`, `${form.name||'Template'} v${idx}`);
    if (!name) return;
    try {
      await API.createTemplate({ name, category:form.category||'Generic', content, variables:[], visibility:form.visibility||'private' });
      load(); setMsg('Variation saved as template!');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  if (loading) return <Spinner />;
  return (
    <div style={{ padding:28, overflowY:'auto', height:'100%' }}>
      {/* AI Generation Modal */}
      {aiGen.open && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Card style={{ width:620, maxHeight:'86vh', display:'flex', flexDirection:'column', borderRadius:16 }}>
            <div style={{ padding:'18px 22px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div>
                <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:C.text }}>🤖 AI Message Variations</h3>
                <p style={{ margin:'3px 0 0', fontSize:12, color:C.muted }}>Generate similar messages to avoid spam detection</p>
              </div>
              <button onClick={()=>setAiGen(g=>({...g,open:false}))} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.muted }}>×</button>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'16px 22px' }}>
              <div style={{ fontSize:12, color:C.muted, background:'#F9FAFB', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
                <div style={{ fontWeight:700, color:C.text, marginBottom:4, fontSize:11 }}>Source message</div>
                {aiGen.sourceContent}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:10, marginBottom:16 }}>
                <div>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>AI Provider</label>
                  <Sel value={aiGen.provider} onChange={e=>setAiGen(g=>({...g,provider:e.target.value}))}>
                    <option value="anthropic">Anthropic (Claude)</option>
                    <option value="openai">OpenAI (GPT)</option>
                  </Sel>
                </div>
                <div>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Variations to generate</label>
                  <Sel value={aiGen.count} onChange={e=>setAiGen(g=>({...g,count:+e.target.value}))}>
                    {[2,3,4,5].map(n=><option key={n} value={n}>{n}</option>)}
                  </Sel>
                </div>
                <div style={{ display:'flex', alignItems:'flex-end' }}>
                  <Btn onClick={runGenerate} disabled={aiGen.loading}>{aiGen.loading?'⏳ Generating...':'⚡ Generate'}</Btn>
                </div>
              </div>
              {aiGen.variations.length > 0 && (
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:10 }}>Generated Variations</div>
                  {aiGen.variations.map((v,i)=>(
                    <div key={i} style={{ background:'#F9FAFB', border:`1px solid ${C.border}`, borderRadius:9, padding:'12px 14px', marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:7 }}>
                        <span style={{ fontSize:11, fontWeight:700, color:C.wa }}>Variation {i+1}</span>
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={()=>{ navigator.clipboard.writeText(v); setMsg('Copied to clipboard!'); }}
                            style={{ background:C.accentBg, border:`1px solid ${C.accent}30`, color:C.accent, borderRadius:6, padding:'3px 10px', fontSize:11, cursor:'pointer', fontFamily:F }}>Copy</button>
                          <button onClick={()=>saveVariation(v,i+1)}
                            style={{ background:C.waLight, border:`1px solid ${C.wa}30`, color:C.waDark, borderRadius:6, padding:'3px 10px', fontSize:11, cursor:'pointer', fontFamily:F }}>Save as Template</button>
                        </div>
                      </div>
                      <div style={{ fontSize:12, color:C.textSub, lineHeight:1.65 }}>{v}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ padding:'12px 22px', borderTop:`1px solid ${C.border}`, background:'#FAFBFC', borderRadius:'0 0 16px 16px', flexShrink:0 }}>
              <Btn variant="ghost" size="sm" onClick={()=>setAiGen(g=>({...g,open:false}))}>Close</Btn>
            </div>
          </Card>
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h2 style={{ fontSize:22, fontWeight:800, color:C.text }}>Templates</h2>
          <p style={{ color:C.muted, fontSize:13, marginTop:4 }}>Reusable message templates with dynamic variables</p>
        </div>
        <Btn onClick={()=>setAddOpen(true)}>+ New Template</Btn>
      </div>
      <Alert msg={err} onClose={()=>setErr('')} />
      <Alert msg={msg} type="success" onClose={()=>setMsg('')} />

      {addOpen && (
        <Card style={{ padding:22, marginBottom:20, border:`2px solid ${C.wa}30` }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>Create Template</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Template Name *</label>
              <Input placeholder="HireMe Initial Outreach" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Category</label>
              <Sel value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                {CATS.map(c=><option key={c}>{c}</option>)}
              </Sel>
            </div>
            <div>
              <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Visibility</label>
              <Sel value={form.visibility} onChange={e=>setForm(f=>({...f,visibility:e.target.value}))}>
                <option value="private">🔒 Private (only me)</option>
                <option value="global">🌐 Global (everyone)</option>
              </Sel>
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Message Content *</label>
            <textarea value={form.content} onChange={e=>setForm(f=>({...f,content:e.target.value}))} rows={4}
              placeholder="Hello {{name}}, I am reaching out from Grepix Infotech..."
              style={{ width:'100%', background:'#F9FAFB', border:`1.5px solid ${C.border}`, borderRadius:9, padding:'9px 14px', color:C.text, fontSize:13, fontFamily:F, resize:'vertical', boxSizing:'border-box' }} />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:5 }}>Variables (comma separated, without braces)</label>
            <Input placeholder="name, city, company" value={form.variables} onChange={e=>setForm(f=>({...f,variables:e.target.value}))} />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={save}>Save Template</Btn>
            <button onClick={()=>form.content?openAiGen(form.content):setErr('Write some message content first')}
              style={{ background:C.purpleBg, border:`1px solid ${C.purple}30`, color:C.purple, borderRadius:9, padding:'8px 16px', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:F }}>
              🤖 Generate Variations
            </button>
            <Btn variant="ghost" onClick={()=>setAddOpen(false)}>Cancel</Btn>
          </div>
        </Card>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        {templates.map(t=>(
          <Card key={t.id} style={{ padding:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{t.name}</div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <Badge label={t.category} color={C.accent} bg={C.accentBg} />
                <button onClick={()=>toggleVisibility(t)} title="Click to toggle visibility"
                  style={{ background:'none', border:'none', cursor:'pointer', padding:0 }}>
                  <Badge label={t.visibility==='global'?'🌐 Global':'🔒 Private'}
                    color={t.visibility==='global'?C.success:C.muted} bg={t.visibility==='global'?C.successBg:'#F3F4F6'} />
                </button>
                <button onClick={()=>openAiGen(t.content)} title="Generate AI variations"
                  style={{ background:'none', border:'none', cursor:'pointer', fontSize:16, lineHeight:1 }}>🤖</button>
                <button onClick={()=>{ if(window.confirm('Delete template?')) API.deleteTemplate(t.id).then(load); }}
                  style={{ background:'none', border:'none', color:C.danger, cursor:'pointer', fontSize:14 }}>🗑</button>
              </div>
            </div>
            <div style={{ fontSize:12, color:C.textSub, lineHeight:1.6, background:'#F9FAFB', borderRadius:8, padding:'10px 12px', border:`1px solid ${C.border}`, marginBottom:10 }}>
              {t.content}
            </div>
            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
              {(t.variables||[]).map(v=>(
                <code key={v} style={{ fontSize:10, background:C.accentBg, color:C.accent, padding:'2px 6px', borderRadius:4 }}>{`{{${v}}}`}</code>
              ))}
            </div>
          </Card>
        ))}
        {templates.length===0 && (
          <Card style={{ padding:40, textAlign:'center', gridColumn:'1/-1' }}>
            <div style={{ fontSize:36, marginBottom:12 }}>📝</div>
            <div style={{ fontSize:15, fontWeight:700, color:C.textSub }}>No templates yet</div>
            <div style={{ fontSize:13, color:C.muted, marginBottom:16 }}>Create reusable message templates for your campaigns</div>
            <Btn onClick={()=>setAddOpen(true)}>+ Create Template</Btn>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── SETTINGS ────────────────────────────────────────────────
function Settings() {
  const { user, logout } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [users, setUsers] = useState([]);
  const [rolePerms, setRolePerms] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [autoReplies, setAutoReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [arOpen, setArOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name:'', email:'', password:'', role:'agent' });
  const [arForm, setArForm] = useState({ keyword:'', match_type:'contains', reply:'', visibility:'private' });
  const [toggles, setToggles] = useState({ workingHours:true, lunchBreak:true, weekendMessages:false, randomBreaks:true, typingSimulation:true });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({ name:'', role:'agent', is_active:1, crm_staff_id:'' });
  const [crmStaff, setCrmStaff] = useState([]);
  const [aiKeys, setAiKeys] = useState({ anthropic:'', openai:'' });

  // Team management, Role Permissions, and Integrations are system-wide — only super_admin can see/call them
  useEffect(()=>{
    const calls = [API.getAutoReplies()];
    if (isSuperAdmin) calls.push(API.getUsers(), API.getIntegrations(), API.getRolePermissions(), API.getLeadStaff());
    Promise.all(calls)
      .then(([a, u, i, rp, stf])=>{
        setAutoReplies(a.data);
        if (isSuperAdmin) { setUsers(u.data); setIntegrations(i.data); setRolePerms(rp.data); setCrmStaff(stf?.data||[]); }
      })
      .finally(()=>setLoading(false));
  }, [isSuperAdmin]);

  const invite = async () => {
    if (!inviteForm.name||!inviteForm.email||!inviteForm.password) return setErr('All fields required');
    try {
      await API.registerUser(inviteForm);
      const r = await API.getUsers(); setUsers(r.data);
      setInviteOpen(false); setInviteForm({ name:'', email:'', password:'', role:'agent' });
      setMsg('Team member added!');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const startEditUser = u => { setEditingUserId(u.id); setEditForm({ name:u.name, role:u.role, is_active:u.is_active, crm_staff_id:u.crm_staff_id||'' }); };

  const saveEditUser = async () => {
    try {
      await API.updateUser(editingUserId, editForm);
      const r = await API.getUsers(); setUsers(r.data);
      setEditingUserId(null);
      setMsg('User updated!');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const removeUser = async id => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await API.deleteUser(id);
      const r = await API.getUsers(); setUsers(r.data);
      setMsg('User deleted!');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const updateRolePerm = async (role, field, value) => {
    try {
      await API.updateRolePermission(role, { [field]: value });
      const r = await API.getRolePermissions(); setRolePerms(r.data);
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const toggleIntegration = async (name, current) => {
    await API.updateIntegration(name, { is_enabled:current?0:1 });
    const r = await API.getIntegrations(); setIntegrations(r.data);
  };

  const saveAiKey = async (provider) => {
    if (!aiKeys[provider]) return;
    try {
      await API.updateIntegration(provider, { is_enabled:1, config:{ api_key:aiKeys[provider] } });
      const r = await API.getIntegrations(); setIntegrations(r.data);
      setAiKeys(k=>({...k,[provider]:''}));
      setMsg(`${provider} API key saved and enabled!`);
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const addAR = async () => {
    if (!arForm.keyword||!arForm.reply) return setErr('Keyword and reply required');
    try {
      await API.createAutoReply(arForm);
      const r = await API.getAutoReplies(); setAutoReplies(r.data);
      setArOpen(false); setArForm({ keyword:'', match_type:'contains', reply:'', visibility:'private' });
      setMsg('Auto-reply saved!');
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const toggleARVisibility = async ar => {
    try {
      await API.updateAutoReply(ar.id, { visibility: ar.visibility==='global'?'private':'global' });
      const r = await API.getAutoReplies(); setAutoReplies(r.data);
    } catch(e) { setErr(e.response?.data?.error||'Failed'); }
  };

  const toggleAR = async (id, current) => {
    await API.updateAutoReply(id, { is_active:current?0:1 });
    const r = await API.getAutoReplies(); setAutoReplies(r.data);
  };

  if (loading) return <Spinner />;

  const INTEG_META = {
    meta_ads:{icon:'🎯',label:'Meta Ads'},google_ads:{icon:'🔍',label:'Google Ads'},
    zoho_crm:{icon:'📦',label:'Zoho CRM'},hubspot:{icon:'🟠',label:'HubSpot'},
    zapier:{icon:'⚡',label:'Zapier'},webhooks:{icon:'🔗',label:'Webhooks'},
    make:{icon:'🟣',label:'Make.com'},pipedrive:{icon:'📊',label:'Pipedrive'},
  };
  const AI_PROVIDERS = { anthropic:{ icon:'🟤', label:'Anthropic (Claude)' }, openai:{ icon:'🟢', label:'OpenAI (GPT)' } };

  return (
    <div style={{ padding:28, overflowY:'auto', height:'100%', maxWidth:860 }}>
      <h2 style={{ fontSize:22, fontWeight:800, color:C.text, marginBottom:24 }}>Settings</h2>
      <Alert msg={err} onClose={()=>setErr('')} />
      <Alert msg={msg} type="success" onClose={()=>setMsg('')} />

      {/* Team — system-wide user management, super_admin only */}
      {isSuperAdmin && (
      <Card style={{ padding:24, marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text }}>👥 Team & Roles</h3>
          <Btn variant="ghost" size="sm" onClick={()=>setInviteOpen(true)}>+ Invite Member</Btn>
        </div>
        {inviteOpen && (
          <div style={{ background:'#F9FAFB', border:`1px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:16 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr auto', gap:10, alignItems:'end' }}>
              {[['Name','name','Full Name'],['Email','email','email@domain.com'],['Password','password','••••••••']].map(([l,k,ph])=>(
                <div key={k}>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>{l}</label>
                  <Input type={k==='password'?'password':'text'} placeholder={ph} value={inviteForm[k]} onChange={e=>setInviteForm(f=>({...f,[k]:e.target.value}))} />
                </div>
              ))}
              <div>
                <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Role</label>
                <Sel value={inviteForm.role} onChange={e=>setInviteForm(f=>({...f,role:e.target.value}))}>
                  <option value="agent">Agent</option>
                  <option value="manager">Manager</option>
                  <option value="super_admin">Super Admin</option>
                </Sel>
              </div>
              <div style={{ display:'flex', gap:6 }}><Btn size="sm" onClick={invite}>Add</Btn><Btn variant="ghost" size="sm" onClick={()=>setInviteOpen(false)}>✕</Btn></div>
            </div>
          </div>
        )}
        {users.map(u=>(
          <div key={u.id} style={{ padding:'12px 0', borderBottom:`1px solid ${C.border}` }}>
            {editingUserId===u.id ? (
              <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr 1.5fr 0.7fr auto', gap:10, alignItems:'end' }}>
                <div>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Name</label>
                  <Input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))} />
                </div>
                <div>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Role</label>
                  <Sel value={editForm.role} onChange={e=>setEditForm(f=>({...f,role:e.target.value}))}>
                    <option value="agent">Agent</option>
                    <option value="manager">Manager</option>
                    <option value="super_admin">Super Admin</option>
                  </Sel>
                </div>
                <div>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>CRM Staff (Lead Access)</label>
                  <Sel value={editForm.crm_staff_id} onChange={e=>setEditForm(f=>({...f,crm_staff_id:e.target.value}))}>
                    <option value="">— No mapping —</option>
                    {crmStaff.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                  </Sel>
                </div>
                <div>
                  <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Active</label>
                  <Toggle val={!!editForm.is_active} onChange={v=>setEditForm(f=>({...f,is_active:v?1:0}))} size={18} />
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <Btn size="sm" onClick={saveEditUser}>Save</Btn>
                  <Btn variant="ghost" size="sm" onClick={()=>setEditingUserId(null)}>✕</Btn>
                </div>
              </div>
            ) : (
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <Av text={u.name.split(' ').map(n=>n[0]).join('').slice(0,2)} size={36} />
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{u.name} {u.id===user?.id&&<span style={{ fontSize:10, color:C.wa }}>(You)</span>}</div>
                    <div style={{ fontSize:11, color:C.muted }}>{u.email}</div>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <Badge label={u.role.replace('_',' ')} color={u.role==='super_admin'?C.purple:u.role==='manager'?C.accent:C.muted}
                    bg={u.role==='super_admin'?C.purpleBg:u.role==='manager'?C.accentBg:'#F3F4F6'} />
                  {u.role==='manager' && (
                    <Badge
                      label={u.crm_staff_id ? `🔗 ${crmStaff.find(s=>s.id==u.crm_staff_id)?.name||'Staff #'+u.crm_staff_id}` : '⚠️ No CRM mapping'}
                      color={u.crm_staff_id ? C.success : C.warning}
                      bg={u.crm_staff_id ? C.successBg : '#FFF8E1'} />
                  )}
                  <Badge label={u.is_active?'Active':'Inactive'} color={u.is_active?C.success:C.muted}
                    bg={u.is_active?C.successBg:'#F3F4F6'} />
                  <button onClick={()=>startEditUser(u)} title="Edit" style={{ background:'none', border:'none', cursor:'pointer', fontSize:14 }}>✏️</button>
                  {u.id!==user?.id && (
                    <button onClick={()=>removeUser(u.id)} title="Delete" style={{ background:'none', border:'none', color:C.danger, cursor:'pointer', fontSize:14 }}>🗑</button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </Card>
      )}

      {/* Role Permissions — capability matrix for manager/agent, super_admin only */}
      {isSuperAdmin && (
      <Card style={{ padding:24, marginBottom:16 }}>
        <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:6 }}>🔐 Role Permissions</h3>
        <div style={{ fontSize:12, color:C.muted, marginBottom:16 }}>Control whether Managers and Agents can see, edit, or delete data belonging to other users. Super Admin always has full access.</div>
        <div style={{ display:'grid', gridTemplateColumns:'80px 1fr 1fr 1fr', gap:10, fontSize:11, color:C.muted, fontWeight:700, marginBottom:8 }}>
          <div>Role</div><div>View</div><div>Edit</div><div>Delete</div>
        </div>
        {rolePerms.map(rp=>(
          <div key={rp.role} style={{ display:'grid', gridTemplateColumns:'80px 1fr 1fr 1fr', gap:10, alignItems:'center', padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
            <Badge label={rp.role.replace('_',' ')} color={rp.role==='manager'?C.accent:C.muted} bg={rp.role==='manager'?C.accentBg:'#F3F4F6'} />
            <Sel value={rp.view_scope} onChange={e=>updateRolePerm(rp.role,'view_scope',e.target.value)} style={{ fontSize:12 }}>
              <option value="own">Own data only</option>
              <option value="all">All users' data</option>
            </Sel>
            <Sel value={rp.edit_scope} onChange={e=>updateRolePerm(rp.role,'edit_scope',e.target.value)} style={{ fontSize:12 }}>
              <option value="own">Own data only</option>
              <option value="all">All users' data</option>
              <option value="none">No edit access</option>
            </Sel>
            <Sel value={rp.delete_scope} onChange={e=>updateRolePerm(rp.role,'delete_scope',e.target.value)} style={{ fontSize:12 }}>
              <option value="own">Own data only</option>
              <option value="all">All users' data</option>
              <option value="none">No delete access</option>
            </Sel>
          </div>
        ))}
      </Card>
      )}

      {/* Anti-ban */}
      <Card style={{ padding:24, marginBottom:16 }}>
        <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>🛡 Anti-Ban Settings</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          {[['workingHours','Send only 9 AM – 7 PM'],['lunchBreak','Lunch break simulation (1–2 PM)'],['weekendMessages','Allow weekend messages'],['randomBreaks','Random breaks every 50 messages'],['typingSimulation','Typing simulation (human-like)']].map(([k,l])=>(
            <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:13, color:C.textSub }}>{l}</span>
              <Toggle val={toggles[k]} onChange={v=>setToggles(p=>({...p,[k]:v}))} size={18} />
            </div>
          ))}
        </div>
      </Card>

      {/* Auto-replies */}
      <Card style={{ padding:24, marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:C.text }}>🤖 Auto-Replies</h3>
          <Btn variant="ghost" size="sm" onClick={()=>setArOpen(true)}>+ Add Rule</Btn>
        </div>
        {arOpen && (
          <div style={{ background:'#F9FAFB', border:`1px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:16 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 2fr 1fr auto', gap:10, alignItems:'end' }}>
              <div>
                <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Keyword</label>
                <Input placeholder="interested" value={arForm.keyword} onChange={e=>setArForm(f=>({...f,keyword:e.target.value}))} />
              </div>
              <div>
                <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Match Type</label>
                <Sel value={arForm.match_type} onChange={e=>setArForm(f=>({...f,match_type:e.target.value}))}>
                  <option value="contains">Contains</option>
                  <option value="exact">Exact</option>
                  <option value="regex">Regex</option>
                </Sel>
              </div>
              <div>
                <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Auto Reply</label>
                <Input placeholder="Great! Let me connect you with our team..." value={arForm.reply} onChange={e=>setArForm(f=>({...f,reply:e.target.value}))} />
              </div>
              <div>
                <label style={{ fontSize:11, color:C.muted, fontWeight:600, display:'block', marginBottom:4 }}>Visibility</label>
                <Sel value={arForm.visibility} onChange={e=>setArForm(f=>({...f,visibility:e.target.value}))}>
                  <option value="private">🔒 Private</option>
                  <option value="global">🌐 Global</option>
                </Sel>
              </div>
              <div style={{ display:'flex', gap:6 }}><Btn size="sm" onClick={addAR}>Add</Btn><Btn variant="ghost" size="sm" onClick={()=>setArOpen(false)}>✕</Btn></div>
            </div>
          </div>
        )}
        {autoReplies.map(ar=>(
          <div key={ar.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
            <div>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:3 }}>
                <code style={{ fontSize:11, background:C.accentBg, color:C.accent, padding:'2px 6px', borderRadius:4 }}>{ar.keyword}</code>
                <Badge label={ar.match_type} color={C.muted} bg="#F3F4F6" />
                <button onClick={()=>toggleARVisibility(ar)} title="Click to toggle visibility"
                  style={{ background:'none', border:'none', cursor:'pointer', padding:0 }}>
                  <Badge label={ar.visibility==='global'?'🌐 Global':'🔒 Private'}
                    color={ar.visibility==='global'?C.success:C.muted} bg={ar.visibility==='global'?C.successBg:'#F3F4F6'} />
                </button>
              </div>
              <div style={{ fontSize:12, color:C.muted, maxWidth:400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ar.reply}</div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <Toggle val={!!ar.is_active} onChange={()=>toggleAR(ar.id,ar.is_active)} size={18} />
              <button onClick={()=>{ if(window.confirm('Delete?')) API.deleteAutoReply(ar.id).then(()=>API.getAutoReplies().then(r=>setAutoReplies(r.data))); }}
                style={{ background:'none', border:'none', color:C.danger, cursor:'pointer' }}>🗑</button>
            </div>
          </div>
        ))}
        {!autoReplies.length && <div style={{ color:C.muted, fontSize:13, padding:'10px 0' }}>No auto-replies configured. Add keyword rules to auto-respond to incoming messages.</div>}
      </Card>

      {/* AI Configuration — API keys for Anthropic / OpenAI, super_admin only */}
      {isSuperAdmin && integrations.some(i=>AI_PROVIDERS[i.name]) && (
      <Card style={{ padding:24, marginBottom:16 }}>
        <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:4 }}>🤖 AI Configuration</h3>
        <div style={{ fontSize:12, color:C.muted, marginBottom:16 }}>Configure AI API keys to generate message variations in Templates.</div>
        {integrations.filter(i=>AI_PROVIDERS[i.name]).map(integ=>{
          const meta = AI_PROVIDERS[integ.name];
          const hasKey = !!(integ.config?.api_key);
          return (
            <div key={integ.id} style={{ padding:'14px 0', borderBottom:`1px solid ${C.border}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{meta.icon} {meta.label}</div>
                  <div style={{ fontSize:11, color:integ.is_enabled?C.success:C.muted }}>
                    {integ.is_enabled?'● Enabled':'○ Disabled'} {hasKey?'· Key configured':'· No key set'}
                  </div>
                </div>
                <Toggle val={!!integ.is_enabled} onChange={()=>toggleIntegration(integ.name,integ.is_enabled)} size={18} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8 }}>
                <Input type="password"
                  placeholder={hasKey?'••••••••• (key saved — type a new one to replace)':`Paste ${meta.label} API key here...`}
                  value={aiKeys[integ.name]||''}
                  onChange={e=>setAiKeys(k=>({...k,[integ.name]:e.target.value}))} />
                <Btn size="sm" onClick={()=>saveAiKey(integ.name)} disabled={!aiKeys[integ.name]}>Save Key</Btn>
              </div>
            </div>
          );
        })}
      </Card>
      )}

      {/* Integrations — platform-wide config, super_admin only */}
      {isSuperAdmin && (
      <Card style={{ padding:24, marginBottom:16 }}>
        <h3 style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>🔗 Integrations</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          {integrations.filter(i=>!AI_PROVIDERS[i.name]).map(integ=>{
            const meta = INTEG_META[integ.name]||{ icon:'🔌', label:integ.name };
            return (
              <div key={integ.id} style={{ background:'#FAFBFC', border:`1px solid ${integ.is_enabled?`${C.wa}40`:C.border}`, borderRadius:10, padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{meta.icon} {meta.label}</div>
                  <div style={{ fontSize:11, color:integ.is_enabled?C.success:C.muted }}>
                    {integ.is_enabled?'● Connected':'○ Not connected'}
                  </div>
                </div>
                <Toggle val={!!integ.is_enabled} onChange={()=>toggleIntegration(integ.name,integ.is_enabled)} size={18} />
              </div>
            );
          })}
        </div>
      </Card>
      )}

      {/* Danger zone */}
      <Card style={{ padding:24, border:`1px solid ${C.danger}30` }}>
        <h3 style={{ fontSize:14, fontWeight:700, color:C.danger, marginBottom:12 }}>⚠️ Account</h3>
        <Btn variant="danger" onClick={()=>{ if(window.confirm('Sign out?')) logout(); }}>Sign Out</Btn>
      </Card>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────
function MainApp() {
  const { user, loading } = useAuth();
  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem('wa_page');
    return NAV.some(n=>n.id===saved) ? saved : 'dashboard';
  });
  useEffect(()=>{ localStorage.setItem('wa_page', page); }, [page]);

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:C.bg }}>
      <Spinner />
    </div>
  );
  if (!user) return <LoginPage />;

  const PAGES = { dashboard:Dashboard, accounts:Accounts, campaigns:Campaigns, inbox:Inbox, leads:LeadsCRM, analytics:Analytics, templates:Templates, settings:Settings };
  const Page = PAGES[page]||Dashboard;

  return (
    <div style={{ display:'flex', height:'100vh', background:C.bg, fontFamily:F, color:C.text, overflow:'hidden' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <Sidebar active={page} onNav={setPage} user={user} />
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Top bar */}
        <div style={{ height:54, borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', background:'#fff', flexShrink:0, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize:15, fontWeight:700, color:C.text, textTransform:'capitalize' }}>
            {page.replace('_',' ')}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ background:C.waLight, border:`1px solid ${C.wa}30`, borderRadius:20, padding:'5px 12px', display:'flex', alignItems:'center', gap:6 }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:C.success }} />
              <span style={{ fontSize:11, fontWeight:700, color:C.waDark }}>Platform Online</span>
            </div>
            <Av text={(user.name||'?').split(' ').map(n=>n[0]).join('').slice(0,2)} size={32} bg={C.waLight} color={C.wa} />
          </div>
        </div>
        <main style={{ flex:1, overflow:'hidden', position:'relative' }}>
          <Page onNav={setPage} />
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return <AuthProvider><MainApp /></AuthProvider>;
}
